import { Box, Typography } from "@mui/material";
import Holidays from "date-holidays";
import { DateTime, Settings } from "luxon";
import {
	createContext,
	type Dispatch,
	type FC,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { LoadingFallback } from "../components/LoadingFallback/LoadingFallback";
import { useDepartmentData } from "../hooks/useDepartmentData";
import { useDepartmentWageData } from "../hooks/useDepartmentWageData";
import { useNurses } from "../hooks/useNurses";
import { useFeatureFlag } from "../hooks/useOpenFeatureFlag";
import { localStorageService } from "../services/LocalStorageService";
import { HOSPITAL_DEMO, NEUTRAL } from "../theme/tokens";
import { trackErrorEvent } from "../utils/analytics";
import type { Suggestion } from "../utils/autoStaffing";
import {
	convertIsoDates,
	deepCloneObject,
	getOtWeekStart,
	getPtoStaffingDays,
	getShiftDisplayDate,
	getShiftTypeFromStartTime,
	setDefaultTimezone,
} from "../utils/dateUtils";
import { formatPersonFullName, isNurseActiveInDepartment } from "../utils/formatters";
import {
	AbsentReason,
	type ActualNurse,
	ConflictErrors,
	type Day,
	type DepartmentLocation,
	DepartmentNames,
	DepartmentType,
	type DepartmentWageDataResponse,
	type EmploymentType,
	type EmrType,
	type HospitalData,
	INHOUSE_ALF_DEPARTMENT_IDS,
	INHOUSE_SNF_DEPARTMENT_IDS,
	isIcuTeleDemoDepartment,
	isRealDemoDepartmentId,
	type Nurse,
	NurseActionsErrorTypes,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	type NurseInShiftDetails,
	NurseRole,
	type NurseTrainingStatus,
	Preferences,
	type ScheduledProcedure,
	type Shift,
	type ShiftTimes,
	ShiftType,
	type SpecificPreference,
	SuggestionAction,
	type TransformedShift,
	WageType,
	type WeekData,
} from "../utils/models";
import axiosWrapper from "../utils/networkWrapper";
import { getRoleGroupsForRole } from "../utils/roles";
import { getShiftIndexByShiftTimes, inverseRolesMap, isHeadNurse, processApiResponse, resolveActiveEmrType, rolesMap } from "../utils/utilFunctions";
import { accessToken, type Department, useAuth } from "./AuthProvider";
import { useAnalytics } from "./analytics/hooks";
import { useChosenDepartment } from "./ChosenDepartment";
import { DEFAULT_CHOSEN_DEPARTMENT } from "./ChosenDepartment/consts";
import { FEATURE_FLAGS } from "./FeatureFlagsProvider";
import { useSnackbar } from "./SnackbarContext";
import { calculateShiftTimes } from "./utils/shift-times";
import { useWeekIndex } from "./WeekIndexProvider";

export interface ShiftIndex {
	week: number;
	shift: number;
	day: number;
}

export interface NurseChanges {
	additions: ShiftIndex[];
	removals: ShiftIndex[];
}

export interface HospitalDataContextType {
	hospitalData: HospitalData;
	setHospitalData: Dispatch<SetStateAction<HospitalData>>;
	swapNurseId: number;
	swapNurseWithId: number;
	swapDayNo: number;
	swapDayNoWith: number;
	swapShiftNo: number;
	chosenDepartment: Department;
	locationsMap: Map<string, DepartmentLocation>;
	timezone: string;
	dateIndexMap: Map<string, { weekIndex: number; dayIndex: number }>;
	setChosenDepartment: Dispatch<SetStateAction<Department>>;
	isSnf: boolean;
	isAlf: boolean;
	isHospital: boolean;
	isAnyDemo: boolean;
	/** Live "real demo" dept (inhouse2__) that runs the actual product features on real data,
	 * unlike isAnyDemo which serves legacy fake/stub data. Mirrors backend is_real_demo_dept. */
	isRealDemo: boolean;
	isHospitalDemo: boolean;
	/** Hospital visuals for a real hospital demo dept; unlike isHospitalDemo, no mock bypasses. */
	isHospitalView: boolean;
	/** The Holland ICU/Telemetry demo specifically — gates its per-area Shift-Insights breakdown. */
	isIcuTeleDemo: boolean;
	isSnfDemo: boolean;
	isAlfDemo: boolean;
	isLowry: boolean;
	isColonial: boolean;
	refreshDepartmentData: (department_id: string, update_week_index: boolean, date?: DateTime, dontUpdateScheduleUntilDate?: boolean) => Promise<void>;
	refreshDepartmentWageData: () => Promise<void>;
	refreshNursesData: (department_id: string, date?: DateTime) => Promise<void>;
	isNurseInFutureShifts: (nurseId: number, date?: string, jobIds?: string[]) => boolean;
	isNurseInFutureRecurringShifts: (nurseId: number, date?: string, jobIds?: string[]) => boolean;
	isAutoScheduler: boolean;
	isInitialLoading: boolean;
	wageData: DepartmentWageDataResponse | null;
	setSwapNurseId: (id: number) => void;
	setSwapNurseWithId: (id: number) => void;
	acceptNurseChanges: (shiftNo: number, dayNo: number, nurseId: number, weekI: number, is_actual: boolean) => void;
	addMessageToNurse: (weekIndex: number, shiftNo: number, dayNo: number, nurseId: number, message: string) => void;
	addNurseToShift: (nurseInShift: NurseInShift, weekIndex: number, dayNo: number, shiftNo: number, addedFromActual?: boolean, isDraftMode?: boolean) => void;
	declineNurseChanges: (shiftNo: number, dayNo: number, nurseId: number, weekI: number, isAutoScheduler: boolean) => void;
	removeNurseFromSpecificDate: (
		weekIndex: number,
		dayNo: number,
		shiftNo: number,
		nurseId: number,
		removedFromActual?: boolean,
		id?: string,
		isDraftMode?: boolean,
		message?: string,
		forceRemove?: boolean,
	) => void;
	updateNurse: (updatedNurse: Nurse) => void;
	moveNurseToSpecificDate: (
		weekIndex: number,
		shiftNo: number,
		dayNo: number,
		otherShiftNo: number,
		otherDayNo: number,
		nurseId: number,
		newNurseId?: string,
	) => void;
	restoreNurseToSpecificDate: (weekIndex: number, dayNo: number, shiftNo: number, nurseId: number, restoredFromActual?: boolean, isDraftMode?: boolean) => void;
	swapNurseFromSpecificDate: () => void;
	setSwapDayNo: (dayNo: number) => void;
	setSwapDayNoWith: (dayNo: number) => void;
	setSwapShiftNo: (shiftNo: number) => void;
	setSwapShiftNoWith: (shiftNo: number) => void;
	getWeekIndexByDateTime: (date: DateTime) => number;
	getDayIndexByDateTime: (date: DateTime) => number;
	getWeekIndexByDate: (date: string | DateTime) => number;
	getDayIndexByDate: (date: string | DateTime) => number;
	getNurseTransformedShifts: (nurseId: number) => TransformedShift[];
	getNurseFormattedName: (nurseId: Nurse["id"]) => string;
	getNurseById: (id: number) => Nurse | undefined;
	scheduledShiftsById: Map<string, NurseInShift>;
	activeBedsideNurse: Nurse | undefined;
	removeNurseCompletelyFromShift: (weekIndex: number, dayNo: number, shiftNo: number, nurseId: number, showActual?: boolean) => void;
	removeNursesShiftsCompletely: (shiftsToRemove: string[]) => void;
	addNewDBNurseShifts: (shiftsToAdd: NurseInShift[]) => void;
	onEnterSwap: (originalShift: number, originalDay: number, targetShift: number, targetDay: number, nurseId: number, nurseWithId: number) => void;
	getScoreColor: (shiftNo: number, dayNo: number) => string;
	reorderNurses: () => void;
	getColorByDifficultyName: (difficulty: string | undefined) => string;
	getNurseNameFromId: (id: number) => string;
	implementedChanges: Record<string, boolean>;
	setImplementedChanges: Dispatch<SetStateAction<Record<string, boolean>>>;
	handleSuggestion: (suggestion: Suggestion, weekIndex: number, shiftNo: number, dayNo: number, shiftTimes?: ShiftTimes) => void;
	weekI: number;
	setWeekI: Dispatch<SetStateAction<number>>;
	getNurseShifts: (nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime) => number;
	prepareDataForAddNurse: (
		hospitalData: HospitalData,
		weekIndex: number,
		dayNo: number,
		shiftNo: number,
		nurseId: number,
		location?: string,
		addedFromActual?: boolean,
		requestId?: string,
		shiftTimes?: ShiftTimes,
		nurseInShiftId?: string,
		shiftBonus?: number,
		isDraftMode?: boolean,
	) => any;
	getNurseWeekendShifts: (nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => number;
	getNurseInShiftDetailsById: (id: string) => NurseInShiftDetails | undefined;
	getNurseRDOs: (nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => number;
	getNurseRole: (nurse: Nurse) => React.ReactNode;
	getNurseRoleEnum: (nurseId: number) => NurseRole | null;
	updateNurseLocation: (id: string, weekIndex: number, shiftIndex: number, dayIndex: number, newLocation: string, actual: boolean) => void;
	updateShiftBonus: (id: string, weekIndex: number, shiftIndex: number, dayIndex: number, newBonus: number, actual?: boolean, isDraftMode?: boolean) => void;
	updateNurseNotes: (id: string, weekIndex: number, shiftIndex: number, dayIndex: number, newNotes: string, actual: boolean) => void;
	updateNurseTimes: (
		nurseInShiftId: string,
		weekIndex: number,
		shiftIndex: number,
		dayIndex: number,
		newStartTime: DateTime,
		newEndTime: DateTime,
		actual: boolean,
		isDraftMode?: boolean,
	) => void;
	updateNurseLocationAndTimes: (params: {
		nurseInShiftId: string;
		weekIndex: number;
		shiftIndex: number;
		dayIndex: number;
		newStartTime?: DateTime;
		newEndTime?: DateTime;
		actual: boolean;
		newLocation?: string;
		newJobId?: string;
		newJobName?: string;
		isDraftMode?: boolean;
	}) => void;
	showErrorSnackbar: (errorMessage: string, trackErrorVals: Record<string, any>, error?: any, autoHideDuration?: number) => void;
	getShiftTimes: (
		day: Day,
		shiftNo: number,
		nurseId: number,
		location?: string,
	) => {
		start: DateTime;
		end: DateTime;
	};
	calculateShiftTimes: (
		day: Pick<Day, "date">,
		shiftNo: number,
		fullNurse: NurseFullRole | undefined,
		location?: string,
		section?: string,
	) => { startTime: DateTime; endTime: DateTime };
	updateNurseChargeStatus: (nurseInShiftId: string, weekIndex: number, shiftIndex: number, dayIndex: number, isCharge: boolean, isDraftMode?: boolean) => void;
	updateNurseTrainingStatus: (nurseInShiftId: string, weekIndex: number, shiftIndex: number, dayIndex: number, status: NurseTrainingStatus) => void;

	transformedShifts: TransformedShift[];
	getShiftDate: (weekIndex: number, shiftNo: number, dayNo: number) => DateTime;
	getWeekRange: (referenceDate: DateTime) => { startDate: string; endDate: string };
	departmentNurseTypes: NurseFullRole[];
	userIdNurseMap: { [userId: string]: Nurse };
	holidayOptions: { name: string; date: string }[];
	federalHolidaysUSASet: Set<string>;
	latestDateInWeeksData: DateTime | undefined;
	roleAverageWage: Partial<Record<NurseFullRole, string>>;
	departmentEmrType: EmrType | undefined;
	getRoleGroupCounts: (shifts: NurseInShift[]) => Record<string, number>;
	getJobGroupCounts: (shifts: NurseInShift[]) => Record<string, number>;
	getDepartmentDisplayName: (departmentId: string) => string;
	getNurseShiftsInTimeFrame: (nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime, isActual?: boolean) => NurseInShift[];
	getEarliestShiftDate: () => DateTime | null;
	isNurseWorking: (nurseId: number, date: DateTime) => boolean;
	findEligibleNurse: (originalNurse: Nurse, hospitalData: HospitalData, removeDate: DateTime) => Nurse | undefined;
	findNurseShiftDetails: (
		originalNurse: Nurse,
		hospitalData: HospitalData,
		removeDate: DateTime,
	) => { weekIndex: number; shiftNo: number; dayNo: number; nurseInShift: NurseInShift } | undefined;
	findDepartmentLocationForNurse: (nurse: NurseInShift, locations: DepartmentLocation[]) => DepartmentLocation | undefined;
	calculateDefaultTimesAndLocationForStaff: (staffId: number) => Array<{
		startTime: string;
		endTime: string;
		location: string;
	}>;
	getNurseToWeeklyShifts: (_: Nurse["id"]) => Shift[];
	isMedicalDepartment: boolean;
}

export const HospitalDataContext = createContext<HospitalDataContextType>({
	hospitalData: {} as HospitalData,
	setHospitalData: () => {},
	swapNurseId: -1,
	swapNurseWithId: -1,
	swapDayNo: -1,
	swapDayNoWith: -1,
	swapShiftNo: -1,
	chosenDepartment: DEFAULT_CHOSEN_DEPARTMENT,
	locationsMap: new Map<string, DepartmentLocation>(),
	timezone: "",
	isSnf: false,
	isAlf: false,
	activeBedsideNurse: undefined,
	isHospital: false,
	isAnyDemo: false,
	isRealDemo: false,
	isHospitalDemo: false,
	isHospitalView: false,
	isIcuTeleDemo: false,
	isSnfDemo: false,
	isAlfDemo: false,
	isLowry: false,
	isColonial: false,
	isAutoScheduler: false,
	isInitialLoading: true,
	wageData: null,
	refreshDepartmentData: async (department_id: string, update_week_index: boolean, date?: DateTime, dontUpdateScheduleUntilDate?: boolean) => {},
	refreshDepartmentWageData: async () => {},
	refreshNursesData: async (department_id: string, date?: DateTime) => {},
	isNurseInFutureShifts: (nurseId: number) => false,
	isNurseInFutureRecurringShifts: (nurseId: number) => false,
	setChosenDepartment: () => {},
	setSwapNurseId: () => {},
	setSwapNurseWithId: () => {},
	acceptNurseChanges: () => {},
	addMessageToNurse: () => {},
	addNurseToShift: (nurseInShift: NurseInShift, weekIndex: number, dayNo: number, shiftNo: number, addedFromActual = false) => {},
	declineNurseChanges: () => {},
	removeNurseFromSpecificDate: () => {},
	updateNurse: () => {},
	moveNurseToSpecificDate: () => {},
	restoreNurseToSpecificDate: () => {},
	swapNurseFromSpecificDate: () => {},
	setSwapDayNo: () => {},
	setSwapDayNoWith: () => {},
	setSwapShiftNo: () => {},
	setSwapShiftNoWith: () => {},
	getWeekIndexByDateTime: () => -1,
	getDayIndexByDateTime: () => -1,
	getWeekIndexByDate: () => -1,
	getDayIndexByDate: () => -1,
	removeNurseCompletelyFromShift: () => {},
	removeNursesShiftsCompletely: () => {},
	addNewDBNurseShifts: () => {},
	getNurseShiftsInTimeFrame: () => [],
	onEnterSwap: () => {},
	getColorByDifficultyName: () => "",
	getScoreColor: () => "",
	reorderNurses: () => {},
	getNurseNameFromId: () => "",
	implementedChanges: {},
	setImplementedChanges: () => {},
	handleSuggestion: () => {},
	weekI: 0,
	setWeekI: () => {},
	getNurseShifts: () => 0,
	getNurseWeekendShifts: () => 0,
	getNurseInShiftDetailsById: () => undefined,
	getNurseRDOs: () => 0,
	getNurseRole: () => <></>,
	getNurseRoleEnum: () => null,
	updateNurseLocation: () => {},
	updateNurseNotes: () => {},
	updateNurseTimes: () => {},
	updateNurseLocationAndTimes: (params) => {},
	updateShiftBonus: () => {},
	showErrorSnackbar: () => {},
	getShiftTimes: () => ({ start: DateTime.now(), end: DateTime.now() }),
	dateIndexMap: new Map(),
	calculateShiftTimes: () => ({ startTime: DateTime.now(), endTime: DateTime.now() }),
	updateNurseChargeStatus: () => {},
	updateNurseTrainingStatus: () => {},
	getNurseTransformedShifts: (nurseId: number) => [],
	getNurseFormattedName: (nurseId: number) => "",
	getNurseById: (id: number) => undefined,
	scheduledShiftsById: new Map<string, NurseInShift>(),
	prepareDataForAddNurse: (
		hospitalData: HospitalData,
		weekIndex: number,
		dayNo: number,
		shiftNo: number,
		nurseId: number,
		location?: string,
		addedFromActual?: boolean,
		requestId?: string,
		shiftTimes?: ShiftTimes,
		nurseInShiftId?: string,
		shiftBonus?: number,
		isDraftMode?: boolean,
	) => {},
	transformedShifts: [],
	getShiftDate: () => DateTime.now(),
	getWeekRange: () => ({ startDate: "", endDate: "" }),
	departmentNurseTypes: [],
	userIdNurseMap: {},
	holidayOptions: [],
	getRoleGroupCounts: () => ({}),
	getJobGroupCounts: () => ({}),
	federalHolidaysUSASet: new Set<string>(),
	latestDateInWeeksData: undefined,
	roleAverageWage: {},
	departmentEmrType: undefined,
	getDepartmentDisplayName: () => "",
	getEarliestShiftDate: () => null,
	isNurseWorking: () => false,
	findEligibleNurse: () => undefined,
	findNurseShiftDetails: () => {
		return undefined;
	},
	findDepartmentLocationForNurse: () => undefined,
	calculateDefaultTimesAndLocationForStaff: () => [{ startTime: "06:00", endTime: "18:00", location: "" }],
	getNurseToWeeklyShifts: (_) => [],
	isMedicalDepartment: false,
});

function processProcs(procs: ScheduledProcedure[], twoShiftDayNight = false): Record<string, Record<string, Array<ScheduledProcedure>>> {
	let proceduresView = localStorage.getItem("proceduresView");
	if (!proceduresView) {
		proceduresView = "Procedure Time";
	}

	const response: Record<string, Record<string, Array<ScheduledProcedure>>> = {};
	for (const proc of procs) {
		const dateToSort = proceduresView === "By Admission" ? proc.departmentArrivalTimeMin : proc.activityDate;
		const endDate = proceduresView === "By Admission" ? proc.departmentArrivalTimeMax : dateToSort.plus({ minutes: 1 });

		// 2-shift Day/Night hospitals (the inhouse2__ real demo) bucket by Day (06:00-18:00) /
		// Night. The 3-shift model (Assuta, legacy InHouse__Hospital_Demo) buckets
		// Morning/Evening/Night at 07:00/15:00/23:00 — so those keep the default path.
		if (twoShiftDayNight) {
			addProcToDayNightShift(response, proc, dateToSort);
			continue;
		}

		let shiftStartTime = getShiftStartTime(dateToSort);
		while (shiftStartTime < endDate) {
			const hoursDiff = addProcToShift(response, proc, shiftStartTime, dateToSort);
			shiftStartTime = shiftStartTime.plus({ hour: hoursDiff });
		}
	}

	const sortedData: Record<string, Record<string, Array<ScheduledProcedure>>> = {};
	for (const outerKey in response) {
		sortedData[outerKey] = {};
		for (const innerKey in response[outerKey]) {
			sortedData[outerKey][innerKey] = response[outerKey][innerKey].sort((a, b) => {
				if (proceduresView === "By Admission") return a.departmentArrivalTimeMin.valueOf() - b.departmentArrivalTimeMin.valueOf();
				return a.activityDate.valueOf() - b.activityDate.valueOf();
			});
		}
	}

	return sortedData;
}

function addProcToShift(
	response: Record<string, Record<string, Array<ScheduledProcedure>>>,
	proc: ScheduledProcedure,
	shiftStartTime: DateTime,
	dateToSort: DateTime,
): number {
	const shiftType = shiftStartTime.hour === 7 ? "Morning" : shiftStartTime.hour === 15 ? "Evening" : "Night";
	const dateString = (shiftStartTime.toISO() as string).split("T")[0];
	if (!response[dateString]) {
		response[dateString] = {};
	}
	const dateDict = response[dateString];
	dateDict[shiftType] = dateDict[shiftType] || [];
	dateDict[shiftType].push(proc);

	if (shiftType === "Morning" && dateToSort.hour === 14 && dateToSort.minute >= 30) {
		dateDict.Evening = dateDict.Evening || [];
		dateDict.Evening.push(proc);
		return 16;
	}
	return 8;
}

// Bucket a procedure into a 2-shift Day/Night hospital by its start hour. Day = 06:00-18:00;
// Night = 18:00-06:00. A Night procedure before 06:00 belongs to the previous calendar day's
// Night shift, matching how the staffing grid buckets with day_start=06:00.
function addProcToDayNightShift(response: Record<string, Record<string, Array<ScheduledProcedure>>>, proc: ScheduledProcedure, dateToSort: DateTime): void {
	const shiftType = getShiftTypeFromStartTime(dateToSort);
	const shiftDate = dateToSort.hour < 6 ? dateToSort.minus({ days: 1 }) : dateToSort;
	const dateString = (shiftDate.toISO() as string).split("T")[0];
	response[dateString] ??= {};
	response[dateString][shiftType] ??= [];
	response[dateString][shiftType].push(proc);
}

function getShiftStartTime(d: DateTime): DateTime {
	const hour = d.hour;
	if (hour >= 23) {
		return d.set({ hour: 23 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	}
	if (hour >= 15) {
		return d.set({ hour: 15 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	}
	if (hour >= 7) {
		return d.set({ hour: 7 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	}

	const newD = d.minus({ days: 1 }).set({ hour: 23 }).set({ minute: 0 }).set({ second: 0 }).set({ millisecond: 0 });
	return newD;
}

function findCurrentWeekIndex(weeks: WeekData[], department_id: string, date?: DateTime, timezone?: string): number {
	// For Cheyanna the schedule starts for the previous schedule week, so we want to start from the second week
	if (department_id === DepartmentNames.AutoScheduler) return 1;

	const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();
	const currentSunday = date ? date.startOf("week") : now.startOf("week");
	const currentSundayStr = currentSunday.toFormat("yyyy-MM-dd");

	// Loop through weeks to find matching Sunday
	const index = weeks.findIndex((week) => {
		const weekSunday = week.weeklyShifts[0].days[0].date.toFormat("yyyy-MM-dd");
		return weekSunday === currentSundayStr;
	});

	// Return 0 if no match found, otherwise return the found index
	return index === -1 ? 0 : index;
}

export const HospitalDataProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { t } = useTranslation();
	const { isLoggedIn, logout, userInfo } = useAuth();

	const [hospitalData, setHospitalData] = useState<HospitalData>({
		patients: [],
		nurses: [],
		weeks: [],
		procedures: {},
		feedbacks: [],
	});

	const { chosenDepartment, setChosenDepartment } = useChosenDepartment();
	const locationsMap = useMemo(() => new Map(chosenDepartment.locations.map((loc) => [loc.id, loc])), [chosenDepartment]);

	const timezone = useMemo(() => chosenDepartment?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone, [chosenDepartment]);
	const isAutoScheduler = useMemo<boolean>(() => chosenDepartment?.department_id === DepartmentNames.AutoScheduler, [chosenDepartment]);
	const isLowry = useMemo<boolean>(() => chosenDepartment?.department_id === DepartmentNames.CenterAtLowryMainDept, [chosenDepartment]);
	const isColonial = useMemo<boolean>(() => chosenDepartment?.department_id === DepartmentNames.ColonialMainDept, [chosenDepartment]);
	const isSnf = useMemo<boolean>(
		() => chosenDepartment?.type === DepartmentType.SNF || INHOUSE_SNF_DEPARTMENT_IDS.includes(chosenDepartment?.department_id),
		[chosenDepartment],
	);
	const isHospital = useMemo<boolean>(() => chosenDepartment?.type === DepartmentType.Hospital, [chosenDepartment]);
	const isAlf = useMemo<boolean>(
		() => chosenDepartment?.type === DepartmentType.ALF || INHOUSE_ALF_DEPARTMENT_IDS.includes(chosenDepartment?.department_id),
		[chosenDepartment],
	);
	const isHospitalDemo = useMemo(() => chosenDepartment?.department_id === DepartmentNames.InHouseHospitalDemo, [chosenDepartment]);
	const shouldReverseNameFormat = useFeatureFlag(FEATURE_FLAGS.NameFormatLastFirst);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const isSnfDemo = useMemo(() => chosenDepartment?.department_id === DepartmentNames.InHouseSNF, [chosenDepartment]);
	const isAlfDemo = useMemo(() => chosenDepartment?.department_id === DepartmentNames.InHouseALF, [chosenDepartment]);
	const isRealDemo = useMemo(() => isRealDemoDepartmentId(chosenDepartment?.department_id), [chosenDepartment]);
	// Hospital visuals for a real hospital demo dept (Patients tab, patient columns, shift
	// labels). Folded into isAnyDemo so the hospital demo gets the same demo treatment as
	// the SNF demo (engagement badges, demo hiring, Management Center demo mode, etc.).
	const isHospitalView = useMemo(() => isHospital && isRealDemo, [isHospital, isRealDemo]);
	// Hospital type + inhouse2 network + the ICU/Tele department. Matched on a name fragment, not the
	// whole id, so both the original "Holland Hospital" seeds and the renamed "ICU/Tele" one are
	// covered; the other inhouse2 hospital demos (Bayview, Lakeside, Riverside...) are not, even
	// though they also end in "Hospital".
	const isIcuTeleDemo = useMemo(() => isIcuTeleDemoDepartment(chosenDepartment?.department_id, isHospitalView), [isHospitalView, chosenDepartment]);

	const [swapNurseId, setSwapNurseId] = useState(-1);
	const [swapNurseWithId, setSwapNurseWithId] = useState(-1);
	const [swapDayNo, setSwapDayNo] = useState(-1);
	const [swapShiftNo, setSwapShiftNo] = useState(-1);
	const [swapShiftNoWith, setSwapShiftNoWith] = useState(-1);
	const [swapDayNoWith, setSwapDayNoWith] = useState(-1);
	const [weekI, setWeekI] = useState<number>(0);
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { showSnackbar } = useSnackbar();
	const [implementedChanges, setImplementedChanges] = useState<Record<string, boolean>>({});
	const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

	const wantDate = chosenDepartment.start_date?.toFormat("yyyy-MM-dd") || DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd");

	const { refetch: refetchDepartmentData } = useDepartmentData(chosenDepartment.department_id, wantDate, timezone);
	const { data: wageData = null, refetch: refetchDepartmentWageData } = useDepartmentWageData(chosenDepartment.department_id, wantDate);
	const { refetch: refetchNurses } = useNurses(chosenDepartment.department_id);

	const getWeekRange = useCallback(
		(referenceDate: DateTime): { startDate: string; endDate: string } => {
			if (!referenceDate.isValid) return { startDate: "", endDate: "" };
			const weekStart = getOtWeekStart(referenceDate, chosenDepartment.week_start_day);
			return {
				startDate: weekStart.toISODate() ?? "",
				endDate: weekStart.plus({ days: 7 }).toISODate() ?? "",
			};
		},
		[chosenDepartment.week_start_day],
	);

	const federalHolidays = useMemo(() => new Holidays("US", { types: ["public"], timezone: timezone }), [timezone]);

	const federalHolidaysUSA = useMemo(() => {
		const allowedHolidays = chosenDepartment.allowed_holidays || [];

		return [
			...federalHolidays.getHolidays(new Date().getFullYear() - 1),
			...federalHolidays.getHolidays(new Date().getFullYear()),
			...federalHolidays.getHolidays(new Date().getFullYear() + 1),
		]
			.filter((holiday) => allowedHolidays.includes(holiday.name))
			.map((holiday) => ({
				...holiday,
				name: holiday.name,
				date: holiday.date.match(/\d{4}-\d{2}-\d{2}/)?.[0] || "", // extract yyyy-MM-dd from holiday.date string
			}));
	}, [federalHolidays, chosenDepartment.allowed_holidays]);

	const federalHolidaysUSASet: Set<string> = useMemo(() => new Set(federalHolidaysUSA.map((h) => h.date)), [federalHolidaysUSA]);

	const religiousHolidays = useMemo(() => {
		return ["Christmas|12-25", "Easter|04-17", "Yom Kippur|10-09", "Feast of the Immaculate Conception|12-08", "Eid al-Fitr|05-03"].flatMap((holiday) => {
			const [name, date] = holiday.split("|");
			const currentYear = new Date().getFullYear();
			return [-1, 0, 1].map((yearOffset) => ({
				date: `${currentYear + yearOffset}-${date}`,
				name,
			}));
		});
	}, []);

	const holidayOptions = useMemo(() => [...federalHolidaysUSA, ...religiousHolidays], [federalHolidaysUSA, religiousHolidays]);

	const departmentNurseTypesCalc = useMemo(() => {
		// In theory, we should use only departmentRoles.
		// However, due to issues we can have nurses with roles that aren't in the department roles, and we want to show them as well
		const departmentRoles = chosenDepartment.roles.map((r) => rolesMap[r]);
		const nursesRoles = new Set(hospitalData.nurses.map((n) => n.role));
		return Array.from(new Set([...departmentRoles, ...nursesRoles]));
	}, [hospitalData.nurses, chosenDepartment.roles]);

	// We are doing this to prevent re-creation if content is the same.
	const departmentNurseTypes = useMemo(() => {
		return departmentNurseTypesCalc;
	}, [departmentNurseTypesCalc]);

	function getShiftDate(weekIndex: number, shiftNo: number, dayNo: number): DateTime {
		return hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].date;
	}

	const calculateShiftTimesWithTZ = useCallback(
		(
			day: Pick<Day, "date">,
			shiftNo: number,
			nurseRole: NurseFullRole | undefined,
			location?: string,
			section?: string,
		): { startTime: DateTime; endTime: DateTime } => {
			return calculateShiftTimes({
				day,
				shiftNo,
				nurseRole,
				chosenDepartment,
				location,
				section,
			});
		},
		[chosenDepartment],
	);

	const getRoleGroupCounts = useCallback(
		(shifts: NurseInShift[]): Record<string, number> => {
			const roleGroupCounts: Record<string, number> = {};

			shifts.forEach((shift) => {
				const roleGroups = getRoleGroupsForRole(shift.role, chosenDepartment);

				roleGroups.forEach((roleGroup) => {
					roleGroupCounts[roleGroup] = (roleGroupCounts[roleGroup] ?? 0) + 1;
				});
			});

			return roleGroupCounts;
		},
		[chosenDepartment],
	);

	const getJobGroupCounts = useCallback((shifts: NurseInShift[]): Record<string, number> => {
		const jobGroupCounts: Record<string, number> = {};
		shifts.forEach((shift) => {
			if (!shift.jobId) return;
			jobGroupCounts[shift.jobId] = (jobGroupCounts[shift.jobId] ?? 0) + 1;
		});
		return jobGroupCounts;
	}, []);

	// Move all useCallback hooks to the top
	const getShiftTimes = useCallback(
		(day: Day, shiftNo: number, nurseId: number, location?: string): { start: DateTime; end: DateTime } => {
			// Use the centralized utility function for consistent shift times across the application
			const nurseRole = hospitalData.nurses.find((n) => n.id === nurseId)?.role || NurseFullRole.RN;
			const { startTime, endTime } = calculateShiftTimesWithTZ(day, shiftNo, nurseRole, location);

			return { start: startTime, end: endTime };
		},
		[hospitalData, calculateShiftTimesWithTZ],
	);

	const nursesMap = useMemo(() => {
		if (hospitalData.nurses?.length) return new Map(hospitalData.nurses.map((nurse) => [nurse.id, nurse]));
		return new Map();
	}, [hospitalData.nurses]);

	const getNurseById = useCallback((id: number): Nurse | undefined => nursesMap.get(id), [nursesMap]);

	const scheduledShiftsById = useMemo(() => {
		const map = new Map<string, NurseInShift>();
		for (const week of hospitalData.weeks ?? []) {
			for (const wshift of week.weeklyShifts) {
				for (const day of wshift.days) {
					for (const n of day.nurses) map.set(n.id, n);
				}
			}
		}
		return map;
	}, [hospitalData.weeks]);

	const getNurseShiftsInTimeFrame = useCallback(
		(nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime, isActual?: boolean) => {
			if (!getNurseById(nurseId) || !hospitalData.weeks) return [];

			const shifts: NurseInShift[] = [];
			hospitalData.weeks.slice(1).forEach((week: WeekData) => {
				week.weeklyShifts.forEach((shift: Shift) => {
					shift.days.forEach((day: Day) => {
						shifts.push(
							...(isActual ? day.nursesActual || [] : day.nurses).filter(
								(n) =>
									n.nurseId === nurseId &&
									!n.isPendingToDelete &&
									(!timeFrameStart || n.endTime.valueOf() > timeFrameStart.valueOf()) &&
									(!timeFrameEnd || n.startTime.valueOf() < timeFrameEnd.valueOf()),
							),
						);
					});
				});
			});

			return shifts.sort((n1, n2) => n1.startTime.valueOf() - n2.startTime.valueOf());
		},
		[hospitalData.weeks, getNurseById],
	);

	const getNurseShifts = useCallback(
		(nurseId: number, timeFrameStart?: DateTime, timeFrameEnd?: DateTime) => {
			return getNurseShiftsInTimeFrame(nurseId, timeFrameStart, timeFrameEnd).length;
		},
		[getNurseShiftsInTimeFrame],
	);

	const calculateDefaultTimesAndLocationForStaff = useCallback(
		(staffId: number): Array<{ startTime: string; endTime: string; location: string }> => {
			const DEFAULT_RESULT = [{ startTime: "06:00", endTime: "18:00", location: "" }];

			const findMostCommonTimesAndLocation = (shifts: NurseInShift[]): Array<{ startTime: string; endTime: string; location: string }> | null => {
				if (shifts.length === 0) return null;

				// Count occurrences of each combined location+time key
				const combinedCounts = new Map<string, number>();

				for (const shift of shifts) {
					if (!shift.startTime || !shift.endTime) continue;
					const location = shift.location ?? "";
					const key = `${location}||${shift.startTime.toFormat("HH:mm")}-${shift.endTime.toFormat("HH:mm")}`;
					combinedCounts.set(key, (combinedCounts.get(key) || 0) + 1);
				}

				if (combinedCounts.size === 0) return null;

				// Sort by count descending and take top 2
				const sorted = [...combinedCounts.entries()].sort((a, b) => b[1] - a[1]);
				const top = sorted.slice(0, 2);

				return top.map(([key]) => {
					const [location, times] = key.split("||");
					const [startTime, endTime] = times.split("-");
					return { startTime, endTime, location };
				});
			};

			const getAllShiftsFromWeeks = (): NurseInShift[] => {
				const allShifts: NurseInShift[] = [];
				hospitalData.weeks.forEach((week: WeekData) => {
					week.weeklyShifts.forEach((shift: Shift) => {
						shift.days.forEach((day: Day) => {
							allShifts.push(...day.nurses.filter((n) => !n.isPendingToDelete));
						});
					});
				});
				return allShifts;
			};

			const nurseId = staffId;
			const nurse = getNurseById(nurseId);

			// Step 1: Look at staff's own shifts
			const nurseShifts = getNurseShiftsInTimeFrame(nurseId);
			const resultFromNurseShifts = findMostCommonTimesAndLocation(nurseShifts);
			if (resultFromNurseShifts) {
				return resultFromNurseShifts;
			}

			// Step 2: If no shifts found for the nurse, look at shifts by role
			if (nurse) {
				const allShifts = getAllShiftsFromWeeks();
				const roleShifts = allShifts.filter((shift) => shift.role === nurse.role);
				const resultFromRoleShifts = findMostCommonTimesAndLocation(roleShifts);
				if (resultFromRoleShifts) {
					return resultFromRoleShifts;
				}
			}

			// Step 3: If no role shifts found, look at all shifts
			const allShifts = getAllShiftsFromWeeks();
			const resultFromAllShifts = findMostCommonTimesAndLocation(allShifts);
			if (resultFromAllShifts) {
				return resultFromAllShifts;
			}

			// Step 4: Default to 6a-6p and empty location
			return DEFAULT_RESULT;
		},
		[hospitalData.weeks, getNurseById, getNurseShiftsInTimeFrame],
	);

	const getNurseInShiftDetailsById = useCallback(
		(id: string): NurseInShiftDetails | undefined => {
			let nurseInShiftDetails: NurseInShiftDetails | undefined;

			hospitalData.weeks.forEach((week: WeekData, weekIndex: number) => {
				week.weeklyShifts.forEach((shift: Shift) => {
					shift.days.forEach((day: Day, dayIndex) => {
						const currentNurse = day.nurses.find((n: NurseInShift) => n.id === id);
						if (currentNurse) {
							nurseInShiftDetails = {
								nurse: currentNurse,
								weekIndex,
								dayIndex,
							};
						}
					});
				});
			});

			return nurseInShiftDetails;
		},
		[hospitalData.weeks],
	);

	const getNurseWeekendShifts = useCallback(
		(nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => {
			const nurse = hospitalData.nurses?.find((n) => n.id === nurseId);
			if (!nurse || !hospitalData.weeks) return 0;

			let weekendShiftCount = 0;
			hospitalData.weeks.slice(1).forEach((week) => {
				week.weeklyShifts.forEach((shift: Shift) => {
					shift.days.forEach((day: Day) => {
						const dayOfWeek = day.date.weekday % 7;
						const nurseInShift: NurseInShift | undefined = day.nurses.find((n) => n.nurseId === nurseId);
						if (
							nurseInShift !== undefined &&
							!nurseInShift.isPendingToDelete &&
							((!dateFrameStart && !dateFrameEnd) ||
								(dateFrameStart &&
									dateFrameEnd &&
									nurseInShift.startTime >= dateFrameStart &&
									nurseInShift.startTime <= dateFrameEnd &&
									nurseInShift.endTime >= dateFrameStart &&
									nurseInShift.endTime <= dateFrameEnd))
						) {
							if (dayOfWeek === 0 || dayOfWeek === 6) {
								weekendShiftCount++;
							} else if (dayOfWeek === 5 && shift.type === "Night") {
								weekendShiftCount++;
							}
						}
					});
				});
			});

			return weekendShiftCount;
		},
		[hospitalData.nurses, hospitalData.weeks, hospitalData],
	);

	const getNurseRDOs = useCallback(
		(nurseId: number, dateFrameStart?: DateTime, dateFrameEnd?: DateTime) => {
			const nurse = hospitalData.nurses?.find((n) => n.id === nurseId);
			if (!nurse) return 0;

			const rdoSet = new Set();
			nurse.employeeSpecificPreferences.forEach((pref: SpecificPreference) => {
				if (pref.type === Preferences.Avoid && pref.data === AbsentReason.RDO) {
					const startDate = pref.start_date;
					const endDate = pref.end_date;
					if (
						dateFrameStart &&
						dateFrameEnd &&
						(!(startDate >= dateFrameStart && startDate <= dateFrameEnd) || !(endDate >= dateFrameStart && endDate <= dateFrameEnd))
					) {
						return;
					}
					// The old rule hardcoded a 07:00 boundary, which is wrong anywhere day_start is not 07:00.
					for (const day of getPtoStaffingDays(startDate, endDate, chosenDepartment.dayStart)) {
						rdoSet.add(day.toFormat("yyyy-MM-dd"));
					}
				}
			});

			return rdoSet.size;
		},
		[hospitalData],
	);

	const getNurseRole = useCallback((nurse: Nurse) => {
		const roleDisplay = inverseRolesMap[nurse.role];

		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Typography variant="body2">{roleDisplay}</Typography>
			</Box>
		);
	}, []);

	const getNurseRoleEnum = useCallback(
		(nurseId: number): NurseRole | null => {
			const nurse = getNurseById(nurseId);
			return nurse ? NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole] : null;
		},
		[getNurseById],
	);

	const activeBedsideNurse = useMemo<Nurse | undefined>(() => getNurseById(userInfo?.nurses_info?.[0]?.nurse?.id), [userInfo, getNurseById]);

	const getNurseFormattedName = useCallback(
		(nurseId: number): string => {
			const nurse: Nurse | undefined = nursesMap.get(nurseId);
			if (!nurse) {
				trackErrorEvent("invalid_nurse_id", `getNurseFormattedName: Nurse not found for id ${nurseId}`, { nurse_id: nurseId });
				return "";
			}
			return formatPersonFullName(nurse, shouldReverseNameFormat);
		},
		[nursesMap, shouldReverseNameFormat],
	);

	const refreshDepartmentWageData = useCallback(async () => {
		if (chosenDepartment.hide_wage) return;
		await refetchDepartmentWageData();
	}, [chosenDepartment.hide_wage, refetchDepartmentWageData]);

	const refreshDepartmentData = async (department_id: string, update_week_index = false, date?: DateTime, dontUpdateScheduleUntilDate?: boolean) => {
		try {
			const [result] = await Promise.all([refetchDepartmentData(), !chosenDepartment.hide_wage ? refetchDepartmentWageData() : Promise.resolve(null)]);

			if (!result.data) return;

			// Iterate over the weeks and their shifts to update the nurses & nursesActual format
			const updatedWeeks = result.data.weeks.map<WeekData>((week: WeekData) => ({
				...week,
				weeklyShifts: week.weeklyShifts.map((shift: Shift) => ({
					...shift,
					days: shift.days.map((day: Day) => ({
						...day,
						nurses: day.nurses?.map((nurse: NurseInShift) => {
							const camelCaseNurse: any = {};
							Object.entries(nurse).forEach(([key, value]) => {
								const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
								camelCaseNurse[camelKey] = value;
							});
							return camelCaseNurse;
						}),
						nursesActual: day.nursesActual?.map((nurse: ActualNurse) => {
							const camelCaseNurse: any = {};
							Object.entries(nurse).forEach(([key, value]) => {
								const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
								camelCaseNurse[camelKey] = value;
							});
							return camelCaseNurse;
						}),
					})),
				})),
			}));

			if (update_week_index) {
				setWeekIndex(findCurrentWeekIndex(updatedWeeks, department_id, date, timezone));
			}

			if (!dontUpdateScheduleUntilDate && result.data.staff_schedule_display_until_date !== undefined) {
				setChosenDepartment((prevDept) => ({
					...prevDept,
					staffScheduleDisplayUntilDate: result.data.staff_schedule_display_until_date || undefined,
				}));
			}

			setHospitalData((prevData) => {
				updateNurses(prevData.nurses, updatedWeeks, shouldReverseNameFormat);

				return {
					...prevData,
					patients: result.data.patients,
					weeks: updatedWeeks,
					units: result.data.units,
					floors: result.data.floors,
				};
			});
		} catch (err) {
			console.error("Failed getting department data:", err);
			showSnackbar(t("error.department.data"), "error");
			await logout();
		}
	};

	const addNewDBNurseShifts = useCallback(
		(shiftsToAdd: NurseInShift[]) => {
			const updateWeeks = (newShift: NurseInShift, weeks: WeekData[]): WeekData[] => {
				return weeks.map((week) => ({
					...week,
					weeklyShifts: week.weeklyShifts.map((shift) => {
						if (shift.type !== newShift.shiftType) {
							return shift;
						}
						return {
							...shift,
							days: shift.days.map((day) => {
								if (
									!day.date
										.startOf("day")
										.equals(getShiftDisplayDate(newShift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day"))
								) {
									return day;
								}
								return {
									...day,
									nurses: [...day.nurses, newShift],
								};
							}),
						};
					}),
				}));
			};

			setHospitalData((prevData: HospitalData) => {
				const newData = { ...prevData };

				const nurseMap = new Map(newData.nurses.map((nurse) => [nurse.id, nurse]));
				const newShifts = shiftsToAdd.map((nurseShift) => {
					const fullNurse = nurseMap.get(nurseShift.nurseId);
					if (fullNurse === undefined) return nurseShift;

					return {
						...nurseShift,
						role: fullNurse.role,
						name: formatPersonFullName(fullNurse, shouldReverseNameFormat),
						type: fullNurse.employmentType as EmploymentType,
						isPending: false,
						isPendingToDelete: false,
						isPublished: true,
					};
				});

				newShifts.forEach((newShift: NurseInShift) => {
					newData.weeks = [...updateWeeks(newShift, newData.weeks)];
				});

				return newData;
			});
		},
		[chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes, shouldReverseNameFormat],
	);

	const removeNursesShiftsCompletely = (shiftsToRemove: string[]) => {
		const shiftsToRemoveSet = new Set(shiftsToRemove);

		const updateWeeks = (weeks: WeekData[]) =>
			weeks.map((week) => ({
				...week,
				weeklyShifts: week.weeklyShifts.map((shift) => ({
					...shift,
					days: shift.days.map((day) => ({
						...day,
						nurses: day.nurses.filter((n) => !shiftsToRemoveSet.has(n.id)),
					})),
				})),
			}));

		setHospitalData((prevData: HospitalData) => ({
			...prevData,
			weeks: updateWeeks(prevData.weeks),
		}));
	};

	const removeNurseCompletelyFromShift = (weekIndex: number, dayNo: number, shiftNo: number, nurseId: number, showActual?: boolean) => {
		setHospitalData((prevData: HospitalData) => {
			const newData = { ...prevData };
			const day = newData.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo];
			if (day) {
				if (showActual) {
					day.nursesActual = day.nursesActual?.filter((nurse: ActualNurse) => nurse.nurseId !== nurseId);
				} else {
					day.nurses = day.nurses.filter((nurse: NurseInShift) => nurse.nurseId !== nurseId);
					let nurseActual = day.nursesActual;
					// in case of removing nurse completelyfrom predicted and actual exists then remove from actual also
					if (nurseActual && nurseActual.length > 0 && nurseActual.some((nurse: ActualNurse) => nurse.nurseId === nurseId)) {
						nurseActual = nurseActual.filter((nurse: ActualNurse) => nurse.nurseId !== nurseId);
						day.nursesActual = nurseActual;
					}
				}
			}
			return newData;
		});
	};

	const isNurseInFutureShifts = (nurseId: number, date?: string, jobIds?: string[]): boolean => {
		return hospitalData.weeks.some((week: WeekData) => {
			return week.weeklyShifts.some((shift: Shift) => {
				return shift.days.some((day: Day) => {
					return (
						day.date.startOf("minute") >= (!date ? DateTime.now().startOf("minute") : DateTime.fromISO(date).startOf("minute")) &&
						day.nurses.some(
							(nurse: NurseInShift) =>
								!nurse.isPendingToDelete && nurse.nurseId === nurseId && (!jobIds?.length || (nurse.jobId && jobIds.includes(nurse.jobId))),
						)
					);
				});
			});
		});
	};

	const isNurseInFutureRecurringShifts = (nurseId: number, date?: string, jobIds?: string[]): boolean => {
		return hospitalData.weeks.some((week: WeekData) => {
			return week.weeklyShifts.some((shift: Shift) => {
				return shift.days.some((day: Day) => {
					return (
						day.date.startOf("minute") >= (!date ? DateTime.now().startOf("minute") : DateTime.fromISO(date).startOf("minute")) &&
						day.nurses.some(
							(nurse: NurseInShift) =>
								!nurse.isPendingToDelete &&
								nurse.nurseId === nurseId &&
								nurse.recurringShiftId &&
								(!jobIds?.length || (nurse.jobId && jobIds.includes(nurse.jobId))),
						)
					);
				});
			});
		});
	};

	const refreshNursesData = async (department_id?: string, date?: DateTime) => {
		try {
			const result = await refetchNurses();
			if (result.data && result.data.length > 0) {
				setHospitalData((prevData) => {
					updateNurses(result.data, prevData.weeks, shouldReverseNameFormat);

					return {
						...prevData,
						nurses: result.data,
						weeks: [...prevData.weeks],
					};
				});
			}
		} catch (err) {
			showSnackbar(t("error.nurses.data"), "error");
		}
	};

	const getProceduresData = async (department_id: string) => {
		axiosWrapper
			.get(`/procedures/by_date?department_id=${department_id}&want_date=${DateTime.now().toFormat("yyyy-MM-dd")}`)
			.then((response) => {
				const procedures = processApiResponse(response.data);
				const proceduresSorted = processProcs(procedures, isHospitalView);
				setHospitalData((prevData) => ({
					...prevData,
					procedures: proceduresSorted,
				}));
			})
			.catch((err) => {
				showSnackbar(t("error.procedures.data"), "error");
			});
	};

	const reorderNurses = () => {
		setHospitalData((prevData) => {
			const procs: ScheduledProcedure[] = Object.values(prevData.procedures)
				.flatMap((innerRecord) => Object.values(innerRecord))
				.flat()
				.reduce<ScheduledProcedure[]>((uniqueProcs, proc) => {
					if (!uniqueProcs.some((p) => p.hashPatientId === proc.hashPatientId)) {
						uniqueProcs.push(proc);
					}
					return uniqueProcs;
				}, []);

			const proceduresSorted = processProcs(procs, isHospitalView);

			return {
				...prevData,
				procedures: proceduresSorted,
			};
		});
	};

	const getfeedbacks = async (department_id: string) => {
		axiosWrapper
			.get(`/departments/weekly_feedbacks?department_id=${department_id}`)
			.then((response) => {
				const feedbacks = processApiResponse(response.data);
				setHospitalData((prevData) => ({
					...prevData,
					feedbacks: feedbacks,
				}));
			})
			.catch((err) => {
				showSnackbar(t("error.feedbacks.data"), "error");
			});
	};

	// Flags refetch without remounting the provider, so a name-order flip in LaunchDarkly would otherwise leave the
	// already-loaded shift names (printout, grids, tooltips) in the old order until the next schedule refresh.
	const appliedNameOrderRef = useRef(shouldReverseNameFormat);
	useEffect(() => {
		if (appliedNameOrderRef.current === shouldReverseNameFormat) return;
		appliedNameOrderRef.current = shouldReverseNameFormat;
		setHospitalData((prevData) => ({ ...prevData, weeks: withShiftNamesInOrder(prevData.nurses, prevData.weeks, shouldReverseNameFormat) }));
	}, [shouldReverseNameFormat]);

	useEffect(() => {
		if (isLoggedIn && userInfo?.departments?.length) {
			setHospitalData((prevData) => ({ ...prevData, departments: userInfo.departments }));
		}
	}, [isLoggedIn, userInfo]);

	const { setUser } = useAnalytics();
	useEffect(() => {
		if (chosenDepartment.department_id && userInfo?.username) {
			setUser({
				userId: userInfo.id,
				departmentId: chosenDepartment.department_id,
				role: userInfo.role,
				firstName: userInfo.first_name,
				// Roles are per-department, so they come off the membership for the department in view,
				// not off the top-level user — the same person can hold different roles per building.
				adminRoles: userInfo.departments?.find((dept) => dept.department_id === chosenDepartment.department_id)?.adminRoles ?? [],
			});
		}
		if (chosenDepartment.department_id) {
			localStorageService.setChosenDepartmentID(chosenDepartment.department_id);
		}
	}, [chosenDepartment, userInfo, setUser]);

	const dateIndexMap = useMemo(() => {
		const newMap = new Map();
		hospitalData.weeks.forEach((week, weekIdx) => {
			week.weeklyShifts?.[0]?.days.forEach((day, dayIdx) => {
				newMap.set(day.date.toFormat("yyyy-MM-dd"), {
					weekIndex: weekIdx,
					dayIndex: dayIdx,
				});
			});
		});
		return newMap;
	}, [hospitalData]);

	const swapNurseFromSpecificDate = () => {
		if (swapNurseId !== -1 && swapNurseWithId !== -1 && swapDayNo !== -1 && swapDayNoWith !== -1 && swapShiftNo !== -1 && swapShiftNoWith !== -1) {
			setHospitalData((prevData) => {
				const currWeek = { ...prevData.weeks[weekIndex] };
				// Get the nurse arrays from both days
				const sourceNurses = currWeek.weeklyShifts[swapShiftNo].days[swapDayNo].nurses;
				const targetNurses = currWeek.weeklyShifts[swapShiftNoWith].days[swapDayNoWith].nurses;

				// Find the indexes of the nurses being swapped
				const sourceNurseIndex = sourceNurses.findIndex((nurse: any) => nurse.nurseId === swapNurseId);
				const targetNurseIndex = targetNurses.findIndex((nurse: any) => nurse.nurseId === swapNurseWithId);

				if (sourceNurseIndex !== -1 && targetNurseIndex !== -1) {
					// Store the original shift and day for both nurses before swapping
					sourceNurses[sourceNurseIndex].originalShift = swapShiftNo;
					sourceNurses[sourceNurseIndex].originalDay = swapDayNo;
					targetNurses[targetNurseIndex].originalShift = swapShiftNoWith;
					targetNurses[targetNurseIndex].originalDay = swapDayNoWith;
					sourceNurses[sourceNurseIndex].isPending = true;
					sourceNurses[sourceNurseIndex].isPendingToSwap = true;
					targetNurses[targetNurseIndex].isPending = true;
					targetNurses[targetNurseIndex].isPendingToSwap = true;
					sourceNurses[sourceNurseIndex].swapWith = swapNurseWithId;
					targetNurses[targetNurseIndex].swapWith = swapNurseId;

					// Swap the nurses
					[sourceNurses[sourceNurseIndex], targetNurses[targetNurseIndex]] = [targetNurses[targetNurseIndex], sourceNurses[sourceNurseIndex]];

					// Mark both nurses as pending for swap
					sourceNurses[sourceNurseIndex].isPendingToSwap = true;
					targetNurses[targetNurseIndex].isPendingToSwap = true;

					sourceNurses[sourceNurseIndex].isPublished = false;
					targetNurses[targetNurseIndex].isPublished = false;

					// Set swapWith to track the swap operation
					sourceNurses[sourceNurseIndex].swapWith = targetNurses[targetNurseIndex].nurseId;
					targetNurses[targetNurseIndex].swapWith = sourceNurses[sourceNurseIndex].nurseId;
				}

				const updatedWeeks = [...prevData.weeks];
				updatedWeeks[weekIndex] = currWeek;
				return { ...prevData, weeks: updatedWeeks };
			});
			resetSwapState();
		}
	};

	useEffect(() => {
		swapNurseFromSpecificDate();
	}, [swapNurseId, swapNurseWithId, swapDayNo, swapDayNoWith, swapShiftNo, swapShiftNoWith]);

	useEffect(() => {
		setDefaultTimezone(chosenDepartment.timezone);
	}, [chosenDepartment.timezone]);

	useEffect(() => {
		if (!isLoggedIn || !userInfo || !chosenDepartment?.department_id || !accessToken) return;

		const loadInitialData = async () => {
			try {
				setIsInitialLoading(true);

				// Department data and nurses are independent — fired together. They cross-merge
				// via updateNurses inside setHospitalData (reading the counterpart from prevData),
				// so order doesn't matter, and nothing reads weeks until isInitialLoading is false.
				const departmentId = isHeadNurse(userInfo.role) ? chosenDepartment?.department_id : undefined;
				const promises = [refreshNursesData(departmentId)];

				if (isHeadNurse(userInfo.role)) {
					promises.push(refreshDepartmentData(chosenDepartment?.department_id, true));
				}

				if (isHospital) {
					promises.push(getProceduresData(chosenDepartment?.department_id), getfeedbacks(chosenDepartment?.department_id));
				}

				await Promise.all(promises);
			} catch (e) {
				console.error(e);
			}

			setIsInitialLoading(false);
		};

		loadInitialData();
		// `accessToken` is a guard, not a trigger: on a switch it updates a render before
		// `chosenDepartment`, so triggering on it would load the old department under the new token (403).
	}, [isLoggedIn, userInfo?.id, chosenDepartment?.department_id, isHospital]);

	useEffect(() => {
		if (!isLoggedIn) return;
		setSwapNurseId(-1);
		setSwapNurseWithId(-1);
		setSwapDayNo(-1);
		setSwapDayNoWith(-1);
		setSwapShiftNo(-1);
		setSwapShiftNoWith(-1);
	}, [weekIndex]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: when the component mounts, meaning the app starts, we set the last refresh timestamp - because other services are already loading data once!
	useEffect(() => {
		localStorageService.resetClientGroupedRefreshData(userInfo.id);
	}, []);

	const addMessageToNurse = useCallback((weekIndex: number, shiftNo: number, dayNo: number, nurseId: number, message: string) => {
		setHospitalData((prevData) => {
			const { day, newData } = copyDayData(prevData, weekIndex, shiftNo, dayNo);

			day.nurses = day.nurses.map((nurse) => (nurse.nurseId !== nurseId ? nurse : { ...nurse, message: message }));

			return newData;
		});
	}, []);

	const getWeekIndexByDateTime = useCallback(
		(date: DateTime) => {
			const staffingDate = getShiftDisplayDate(date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day");
			// Guard against partially-hydrated weeks during refresh: the parent flow can read
			// week ranges before `weeklyShifts[0].days` is populated, throwing on `.date`.
			return hospitalData.weeks.findIndex((week) => {
				const days = week.weeklyShifts[0]?.days;
				if (!days?.length) return false;
				return days[0].date <= staffingDate && days[days.length - 1].date >= staffingDate;
			});
		},
		[hospitalData.weeks, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const getDayIndexByDateTime = useCallback(
		(date: DateTime) => {
			const weekIndex = getWeekIndexByDateTime(date);
			if (weekIndex === -1) {
				return -1;
			}
			const staffingDate = getShiftDisplayDate(date, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes).startOf("day");
			return hospitalData.weeks[weekIndex].weeklyShifts[0].days.findIndex((day) => day.date.valueOf() === staffingDate.valueOf());
		},
		[getWeekIndexByDateTime, hospitalData.weeks, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const getWeekIndexByDate = useCallback(
		(date: string | DateTime) => {
			const dateTime = (typeof date === "string" ? DateTime.fromFormat(date, "yyyy-MM-dd") : date).startOf("day");
			// See note on `getWeekIndexByDateTime` — same partially-hydrated week guard.
			return hospitalData.weeks.findIndex((week) => {
				const days = week.weeklyShifts[0]?.days;
				if (!days?.length) return false;
				return days[0].date <= dateTime && days[days.length - 1].date >= dateTime;
			});
		},
		[hospitalData.weeks],
	);

	const getDayIndexByDate = useCallback(
		(date: string | DateTime) => {
			const dateTime = (typeof date === "string" ? DateTime.fromFormat(date, "yyyy-MM-dd") : date).startOf("day");
			const weekIndex = getWeekIndexByDate(dateTime);

			if (weekIndex === -1) return -1;

			return hospitalData.weeks[weekIndex].weeklyShifts[0].days.findIndex((day) => day.date.valueOf() === dateTime.valueOf());
		},
		[hospitalData.weeks, getWeekIndexByDate],
	);

	const acceptNurseChanges = useCallback((shiftNo: number, dayNo: number, nurseId: number, weekI: number, is_actual: boolean) => {
		setHospitalData((prevData) => {
			const currWeek = { ...prevData.weeks[weekI] };
			const targetShift = currWeek?.weeklyShifts[shiftNo]?.days[dayNo];

			if (is_actual) {
				return acceptActualChange(prevData, currWeek, targetShift, nurseId, weekI, shiftNo, dayNo);
			}

			const nurse = targetShift.nurses.find((n: any) => n.nurseId === nurseId);

			// If nurse doesn't exist, add them
			if (!nurse) {
				console.log(`Error tried to acceptNurseChanges for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}, is_actual ${is_actual}`);
				return prevData;
			}

			// Handle move scenario
			if (nurse.isPendingToMove && nurse.originalShift !== undefined && nurse.originalDay !== undefined) {
				const otherShift = currWeek?.weeklyShifts[nurse.originalShift]?.days[nurse.originalDay];
				const otherNurse = otherShift?.nurses.find((n: any) => n.nurseId === nurse?.swapWith);
				if (otherNurse === undefined) {
					console.log(`Error tried to find other nurse for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}, is_actual ${is_actual}`);
					return prevData;
				}

				let [addedNurse, removedNurse] = [nurse, otherNurse];
				let addedShift = targetShift;
				let removedShift = otherShift;
				if (nurse.isPendingToDelete) {
					[addedNurse, removedNurse] = [otherNurse, nurse];
					addedShift = otherShift;
					removedShift = targetShift;
				}

				addedShift.nurses = addedShift.nurses.filter((n: any) => n.nurseId !== addedNurse.nurseId);
				removedShift.nurses = removedShift.nurses.filter((n: any) => n.nurseId !== removedNurse.nurseId);

				addedShift.nurses.push({
					...addedNurse,
					isPending: false,
					isPendingToMove: false,
					isPublished: true,
				});
				removedShift.nurses.push({
					...removedNurse,
					isPending: false,
					isPendingToMove: false,
					isPublished: true,
				});
			}
			// Handle swap scenario
			else if (nurse.isPendingToSwap && nurse.swapWith !== undefined && nurse.originalShift !== undefined && nurse.originalDay !== undefined) {
				const originalShift = currWeek?.weeklyShifts[nurse.originalShift]?.days[nurse.originalDay];
				const swapNurse = originalShift?.nurses.find((n: any) => n.nurseId === nurse?.swapWith);

				if (swapNurse) {
					// Remove both nurses from their current positions
					targetShift.nurses = targetShift.nurses.filter((n: any) => n.nurseId !== nurseId);
					originalShift.nurses = originalShift.nurses.filter((n: any) => n.nurseId !== nurse?.swapWith);

					// Add them to their new positions
					targetShift.nurses.push({
						...nurse,
						isPending: false,
						isPendingToSwap: false,
						isPublished: true,
					});
					originalShift.nurses.push({
						...swapNurse,
						isPending: false,
						isPendingToSwap: false,
						isPublished: true,
					});
				}
			}
			// Handle removal scenario
			else if (nurse.isPendingToDelete) {
				targetShift.nurses = targetShift.nurses.filter((n: any) => n.nurseId !== nurseId);
			}
			// Handle regular addition or confirmation scenario
			else {
				nurse.isPending = false;
				nurse.isPublished = true;
			}

			nurse.swapWith = undefined;
			nurse.originalShift = undefined;
			nurse.originalDay = undefined;

			const updatedWeeks = [...prevData.weeks];
			updatedWeeks[weekI] = currWeek;
			return { ...prevData, weeks: updatedWeeks };
		});
	}, []);

	const handleNurseDiscard = (
		newData: HospitalData,
		weekIndex: number,
		nurse: NurseInShift,
		nurseShiftNo: number,
		nurseDayNo: number,
		otherNurse: NurseInShift,
		otherShiftNo: number,
		otherDayNo: number,
	) => {
		// Reset original nurse record
		const [nurseToKeep, nurseToRemove, shiftNo, dayNo] = nurse.isPendingToDelete
			? [nurse, otherNurse, otherShiftNo, otherDayNo]
			: [otherNurse, nurse, nurseShiftNo, nurseDayNo];
		nurseToKeep.isPending = false;
		nurseToKeep.isPendingToDelete = false;
		nurseToKeep.isPublished = true;

		// Remove new nurse record
		const nurseIndex = newData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n: NurseInShift) => n.nurseId === nurseToRemove.nurseId);
		newData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].nurses.splice(nurseIndex, 1);
		nurseToRemove.isPending = false;
		nurseToRemove.isPublished = true;
		nurseToRemove.isPendingToDelete = false;
	};

	const handleNurseDiscardAfterSave = (nurse: NurseInShift, otherNurse: NurseInShift) => {
		// Reset original nurse record
		const [nurseToKeep, nurseToRemove] = nurse.isPendingToDelete ? [nurse, otherNurse] : [otherNurse, nurse];

		// original record
		nurseToKeep.isPending = true;
		nurseToKeep.isPublished = false;
		nurseToKeep.isPendingToDelete = false;
		nurseToKeep.isDiscard = true;

		// new record
		nurseToRemove.isPendingToDelete = true;
		nurseToRemove.isPublished = false;
		nurseToRemove.isPending = false;
		nurseToRemove.isDiscard = true;
	};

	const handleDeclineChangeAutoScheduler = (
		newData: HospitalData,
		weekIndex: number,
		nurse: NurseInShift,
		nurseShiftNo: number,
		nurseDayNo: number,
		otherNurse: NurseInShift,
		otherShiftNo: number,
		otherDayNo: number,
	) => {
		// Clear pending move flags
		nurse.isPendingToMove = false;
		otherNurse.isPendingToMove = false;

		if (nurse.isPending && !nurse.isPublished) {
			// Handle discarding changes before they were saved
			handleNurseDiscard(newData, weekIndex, nurse, nurseShiftNo, nurseDayNo, otherNurse, otherShiftNo, otherDayNo);
		} else if (nurse.isPublished) {
			// Handle discarding changes after they were saved
			handleNurseDiscardAfterSave(nurse, otherNurse);
		}
	};

	const declineNurseChanges = useCallback((shiftNo: number, dayNo: number, nurseId: number, weekI = weekIndex, isAutoScheduler = false) => {
		setHospitalData((prevData) => {
			const { day, newData } = copyDayData(prevData, weekI, shiftNo, dayNo);

			// Find the nurse who initiated the swap
			const nurseIndex = findNurseIndexInShift(newData, weekI, shiftNo, dayNo, nurseId);
			day.nurses[nurseIndex] = { ...day.nurses[nurseIndex] };
			const nurse = day.nurses[nurseIndex];

			if (nurse?.isPendingToMove) {
				let otherShift: number | undefined = nurse.originalShift as number;
				let otherDay: number | undefined = nurse.originalDay as number;
				if (otherShift === null || otherDay === null) {
					// get other shift and day properties
					const otherShiftAndDay = newData.weeks[weekI]?.weeklyShifts[shiftNo].days
						.map((day, dayIndex) => {
							if (dayIndex === dayNo) return null;
							const foundNurse = day.nurses.find((n) => n.nurseId === nurseId && n.originalShift === shiftNo && n.originalDay === dayNo);
							if (foundNurse) return [shiftNo, dayIndex];
							return null;
						})
						.filter((d) => d !== null)[0];
					if (otherShiftAndDay !== null) {
						[otherShift, otherDay] = otherShiftAndDay;
					} else {
						console.log(`Error tried to find other shift and day for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}`);
						throw new Error(`Could not find other shift and day for nurse ${nurseId} in week ${weekI}, shift ${shiftNo}, day ${dayNo}`);
					}
				}
				const otherNurseIndex = findNurseIndexInShift(newData, weekI, otherShift as number, otherDay as number, nurseId);
				if (otherNurseIndex !== -1) {
					const otherDayShift = newData.weeks[weekI].weeklyShifts[otherShift as number].days[otherDay as number];
					otherDayShift.nurses[otherNurseIndex] = {
						...otherDayShift.nurses[otherNurseIndex],
					};
					const otherNurse = otherDayShift.nurses[otherNurseIndex];

					if (isAutoScheduler) {
						if (nurse.isPending) {
							handleDeclineChangeAutoScheduler(newData, weekI, nurse, shiftNo, dayNo, otherNurse, otherShift, otherDay);
						}
					} else {
						let isSwapped = false;
						if (nurse.isPendingToDelete) {
							isSwapped = true;
						}
						nurse.isPendingToMove = false;
						nurse.originalShift = undefined;
						nurse.originalDay = undefined;
						nurse.isPending = false;
						nurse.isPendingToDelete = false;
						otherNurse.isPendingToMove = false;
						otherNurse.originalShift = undefined;
						otherNurse.originalDay = undefined;
						otherNurse.isPending = false;
						otherNurse.isPendingToDelete = false;

						if (isSwapped) {
							const tempShiftNo = shiftNo;
							const tempDayNo = dayNo;
							shiftNo = otherShift as number;
							dayNo = otherDay as number;
							otherShift = tempShiftNo;
							otherDay = tempDayNo;
						}

						if (nurse.isPendingToDelete) {
							const nurseIndex = newData?.weeks[weekI]?.weeklyShifts[otherShift as number].days[otherDay as number].nurses.findIndex(
								(n) => n.nurseId === nurseId,
							);
							newData?.weeks[weekI]?.weeklyShifts[otherShift as number].days[otherDay as number].nurses.splice(nurseIndex, 1);
						} else {
							const nurseIndex = newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n) => n.nurseId === nurseId);
							newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.splice(nurseIndex, 1);
						}
					}
				}
			} else if (!nurse || !nurse.swapWith) {
				if (nurse !== undefined) {
					if (nurse.isPendingToDelete) {
						// Keep the nurse and remove the pending status
						nurse.isPending = false;
						nurse.isPendingToDelete = false;
					} else {
						// Remove the nurse if pending for addition
						const nurseIndex = newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n) => n.nurseId === nurseId);
						newData?.weeks[weekI]?.weeklyShifts[shiftNo].days[dayNo].nurses.splice(nurseIndex, 1);
					}
				}
			} else if (nurse?.swapWith) {
				const swapNurseIndex = findNurseIndexInShift(newData, weekI, nurse.originalShift as number, nurse.originalDay as number, nurse.swapWith);
				if (swapNurseIndex !== -1) {
					// Return both nurses to their original shift and day
					moveNurse(newData, weekI, shiftNo, dayNo, nurse.originalShift as number, nurse.originalDay as number, nurseId);
					moveNurse(newData, weekI, nurse.originalShift as number, nurse.originalDay as number, shiftNo, dayNo, nurse.swapWith);
				}
				if (nurse.isPendingToDelete) {
					nurse.isPendingToDelete = false;
					nurse.isPending = false;
				}
			}

			return newData;
		});
	}, []);

	const resetNurseProperties = (nurse: any) => {
		return {
			...nurse,
			isPending: false,
			isPendingToSwap: false,
			isPendingToDelete: false,
			swapWith: undefined,
			originalShift: undefined,
			originalDay: undefined,
			isPublished: false,
			hasAcceptedSwap: false,
		};
	};

	const moveNurse = (data: any, weekIdx: number, fromShiftNo: number, fromDayNo: number, toShiftNo: number, toDayNo: number, nurseId: number) => {
		// Check if the referenced objects exist
		if (data.weeks?.[weekIdx]?.weeklyShifts?.[fromShiftNo]?.days?.[fromDayNo] && data.weeks?.[weekIdx]?.weeklyShifts?.[toShiftNo]?.days?.[toDayNo]) {
			const fromNurses = data.weeks[weekIdx].weeklyShifts[fromShiftNo].days[fromDayNo].nurses;
			const toNurses = data.weeks[weekIdx].weeklyShifts[toShiftNo].days[toDayNo].nurses;

			const nurseIdx = fromNurses.findIndex((nurse: any) => nurse.nurseId === nurseId);
			if (nurseIdx !== -1) {
				const nurse = fromNurses.splice(nurseIdx, 1)[0];
				toNurses.push(resetNurseProperties(nurse));
			}
		} else {
			// Handle the undefined case, maybe log an error or throw an exception
			console.error("Attempted to move a nurse, but the shift or day was not found");
		}
	};

	const onEnterSwap = (originalShift: number, originalDay: number, targetShift: number, targetDay: number, nurseId: number, nurseWithId: number) => {
		setSwapDayNo(originalDay);
		setSwapShiftNo(originalShift);
		setSwapNurseId(nurseWithId);
		setSwapDayNoWith(targetDay);
		setSwapShiftNoWith(targetShift);
		setSwapNurseWithId(nurseId);
	};

	const findNurseIndexInShift = (data: HospitalData, weekIndex: number, shiftNo: number, dayNo: number, nurseId: number) => {
		return data.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].nurses.findIndex((n: any) => n.nurseId === nurseId);
	};

	const resetSwapState = () => {
		setSwapNurseId(-1);
		setSwapNurseWithId(-1);
		setSwapDayNo(-1);
		setSwapDayNoWith(-1);
		setSwapShiftNo(-1);
		setSwapShiftNoWith(-1);
	};

	const prepareDataForAddNurse = (
		hospitalData: HospitalData,
		weekIndex: number,
		dayNo: number,
		shiftNo: number,
		nurseId: number,
		location?: string,
		addedFromActual = false,
		requestId?: string,
		shiftTimes?: ShiftTimes,
		nurseInShiftId?: string,
		shiftBonus?: number,
		isDraftMode?: boolean,
	): NurseInShift | undefined => {
		const fullNurse = hospitalData.nurses.find((n) => n.id === Number(nurseId));
		if (!fullNurse) return undefined;

		const nurseShiftTimes = shiftTimes
			? { startTime: shiftTimes.start, endTime: shiftTimes.end }
			: calculateShiftTimesWithTZ(hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo], shiftNo, fullNurse.role, location);

		const nurseToAdd: NurseInShift = {
			id: nurseInShiftId ?? uuidv4(),
			nurseId: Number(nurseId),
			departmentId: chosenDepartment.department_id,
			isPending: isDraftMode ? true : isHospital,
			isPendingToDelete: false,
			isPendingToSwap: false,
			isPublished: true,
			isPendingToMove: false,
			startTime: nurseShiftTimes.startTime,
			endTime: nurseShiftTimes.endTime,
			role: fullNurse.role,
			name: formatPersonFullName(fullNurse, shouldReverseNameFormat),
			charge: fullNurse.isShiftManager,
			type: fullNurse.employmentType as EmploymentType,
			location: location,
			// Callers that pass explicit shiftTimes (the profile's single-shift add) may target a date
			// OUTSIDE the loaded weeks, where getWeekIndexByDate returns -1 and weeks[-1] is undefined.
			// Those callers already resolved the shift type, so honour it and only fall back to the
			// week lookup for callers that rely on the grid's own data.
			shiftType: shiftTimes?.shiftType ?? hospitalData.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type,
			notes: "",
			bonus: shiftBonus || 0,
			breaks: [],
			requestId,
			trainingStatus: fullNurse.trainingStatus,
			jobId: fullNurse.jobs?.[0]?.id,
			jobName: fullNurse.jobs?.[0]?.displayName,
		};

		if (addedFromActual) {
			nurseToAdd.endTime = nurseToAdd.startTime;
		}

		return nurseToAdd;
	};

	const addNurseToShift = (
		nurseInShift: NurseInShift,
		weekIndex: number,
		dayNo: number,
		shiftNo: number,
		addedFromActual = false,
		isDraftMode = false,
	): void => {
		// weekIndex is -1 when the shift's date falls outside the loaded weeks (the profile's X-week
		// grid can add to a week the staffing data was never fetched for). There is no local day to
		// patch, and copyDayData would index weeks[-1] and crash the whole provider mid-render — so
		// skip the optimistic update and let the next fetch bring the shift in.
		if (shiftNo !== -1 && weekIndex !== -1) {
			setHospitalData((oldData) => {
				const { day, newData } = copyDayData(oldData, weekIndex, shiftNo, dayNo);

				// Set isPending if in draft mode
				const nurseToAdd = isDraftMode ? { ...nurseInShift, isPending: true } : nurseInShift;

				const targetNurseArray = addedFromActual ? day.nursesActual : day.nurses;
				if (targetNurseArray && !targetNurseArray.some((nurse) => nurse.nurseId === Number(nurseToAdd.nurseId))) {
					if (addedFromActual) {
						day.nursesActual = [...targetNurseArray, nurseToAdd];
					} else {
						day.nurses = [...targetNurseArray, nurseToAdd];
					}

					// in case it's predicted and actual exists then add to actual also
					if (!addedFromActual) {
						const nursesActual = day?.nursesActual;
						if (nursesActual && nursesActual.length > 0 && !nursesActual.some((nurse) => nurse.nurseId === Number(nurseToAdd.nurseId))) {
							day.nursesActual = [...nursesActual, nurseToAdd];
						}
					}
				}

				return newData;
			});
		}
	};

	const updateNurse = useCallback(
		(updatedNurse: Nurse) => {
			const filteredNurses: Nurse[] = hospitalData.nurses.filter((nurse: Nurse) => nurse.id !== updatedNurse.id);
			setHospitalData((prevData) => {
				return { ...prevData, nurses: [...filteredNurses, updatedNurse] };
			});
		},
		[hospitalData.nurses],
	);

	const removeNurseFromSpecificDate = useCallback(
		(
			weekIndex: number,
			dayNo: number,
			shiftNo: number,
			nurseIdToRemove: number,
			removeFromActual = false,
			id?: string,
			isDraftMode = false,
			message?: string,
			forceRemove = false,
		) => {
			if (dayNo !== -1 && shiftNo !== -1) {
				setHospitalData((prevData) => {
					const { day, newData } = copyDayData(prevData, weekIndex, shiftNo, dayNo);

					// In draft mode, always mark as pending to delete instead of removing.
					// `forceRemove` bypasses pending-mode (used for agency: scheduled and actual
					// rows mirror each other, so leaving a strikethrough on the other side
					// would still show as "no punch" — see INH-3359).
					const shouldMarkPending = !forceRemove && (isDraftMode || (!isSnf && !isRealDemo));

					if (removeFromActual) {
						if (shouldMarkPending) {
							if (id) {
								day.nursesActual = day.nursesActual?.map((nurse) =>
									nurse.id !== id
										? nurse
										: {
												...nurse,
												isPending: true,
												isPendingToDelete: true,
												isPublished: false,
												message: message,
											},
								);
							} else {
								day.nursesActual = day.nursesActual?.map((nurse) =>
									nurse.nurseId !== nurseIdToRemove
										? nurse
										: {
												...nurse,
												isPending: true,
												isPendingToDelete: true,
												isPublished: false,
												message: message,
											},
								);
							}
						} else {
							// Only actually remove if not in draft mode and is SNF
							if (id) {
								day.nursesActual = day.nursesActual?.filter((nurse) => nurse.id !== id);
							} else {
								day.nursesActual = day.nursesActual?.filter((nurse) => nurse.nurseId !== nurseIdToRemove);
							}
						}
					} else {
						if (shouldMarkPending) {
							if (id) {
								day.nurses = day.nurses.map((nurse) =>
									nurse.id !== id
										? nurse
										: {
												...nurse,
												isPending: true,
												isPublished: false,
												isPendingToDelete: true,
												message: message,
											},
								);
							} else {
								day.nurses = day.nurses.map((nurse) =>
									nurse.nurseId !== nurseIdToRemove
										? nurse
										: {
												...nurse,
												isPending: true,
												isPublished: false,
												isPendingToDelete: true,
												message: message,
											},
								);
							}

							// in case of removing nurse from predicted and actual exists then mark pending delete in actual also
							const nursesActual = day?.nursesActual;
							if (nursesActual && nursesActual.length > 0) {
								day.nursesActual = day.nursesActual?.map((nurse) =>
									nurse.nurseId !== nurseIdToRemove
										? nurse
										: {
												...nurse,
												isPending: true,
												isPendingToDelete: true,
												isPublished: false,
											},
								);
							}
						} else {
							// Only actually remove if not in draft mode and is SNF
							if (id) {
								day.nurses = day.nurses.filter((nurse) => nurse.id !== id);
								day.nursesActual = day.nursesActual?.filter((nurse) => nurse.id !== id);
							} else {
								day.nurses = day.nurses.filter((nurse) => nurse.nurseId !== nurseIdToRemove);
								day.nursesActual = day.nursesActual?.filter((nurse) => nurse.nurseId !== nurseIdToRemove);
							}
						}
					}

					return newData;
				});
			}
		},
		[isSnf, isRealDemo],
	);

	const moveNurseToSpecificDate = (
		weekIndex: number,
		fromShiftNo: number,
		fromDayNo: number,
		toShiftNo: number,
		toDayNo: number,
		nurseId: number,
		newNurseId?: string,
	) => {
		const allShiftsAndDaysValid = [fromShiftNo, fromDayNo, toShiftNo, toDayNo].every((num) => num !== -1);
		if (allShiftsAndDaysValid) {
			setHospitalData((prevData) => {
				const currWeek = { ...prevData.weeks[weekIndex] };

				// current record
				const nurseIndex = currWeek.weeklyShifts[fromShiftNo].days[fromDayNo].nurses.findIndex((nurse: any) => nurse.nurseId === nurseId);
				if (nurseIndex !== -1) {
					const nurse = currWeek.weeklyShifts[fromShiftNo].days[fromDayNo].nurses[nurseIndex];
					nurse.isPending = true;
					nurse.isPendingToDelete = isHospital;
					nurse.isPendingToMove = true;
					nurse.isPublished = false;
					nurse.originalShift = toShiftNo;
					nurse.originalDay = toDayNo;
				}

				if (!currWeek.weeklyShifts[toShiftNo].days[toDayNo].nurses.some((nurse: any) => nurse.nurseId === nurseId)) {
					const fullNurse = prevData.nurses.find((n) => n.id === nurseId);
					if (!fullNurse) return prevData;

					const day = hospitalData?.weeks[weekIndex]?.weeklyShifts[toShiftNo]?.days[toDayNo];
					const defaultTimes = calculateShiftTimesWithTZ(day, toShiftNo, fullNurse.role);

					// new record
					const nurseToAdd: NurseInShift = {
						id: newNurseId ?? uuidv4(),
						nurseId: nurseId,
						departmentId: chosenDepartment.department_id,
						isPending: true,
						isPendingToDelete: false,
						isPendingToSwap: false,
						isPublished: false,
						isPendingToMove: true,
						originalShift: fromShiftNo,
						originalDay: fromDayNo,
						startTime: defaultTimes.startTime,
						endTime: defaultTimes.endTime,
						role: fullNurse.role,
						name: formatPersonFullName(fullNurse, shouldReverseNameFormat),
						charge: fullNurse.isShiftManager,
						type: fullNurse.employmentType as EmploymentType,
						shiftType: currWeek.weeklyShifts[toShiftNo].type,
						notes: "",
						trainingStatus: fullNurse.trainingStatus,
						bonus: 0,
						breaks: [],
					};

					currWeek.weeklyShifts[toShiftNo].days[toDayNo].nurses.push(nurseToAdd);
				}
				const updatedWeeks = [...prevData.weeks];
				updatedWeeks[weekIndex] = currWeek;
				return { ...prevData, weeks: updatedWeeks };
			});
		}
	};

	const restoreNurseToSpecificDate = (
		weekIndex: number,
		dayNo: number,
		shiftNo: number,
		nurseIdToRestore: number,
		restoredFromActual = false,
		isDraftMode?: boolean,
	) => {
		if (dayNo !== -1 && shiftNo !== -1) {
			const tempData = { ...hospitalData };

			if (restoredFromActual) {
				const nursesActual = tempData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].nursesActual;
				const nurseIndex = nursesActual?.findIndex((nurse) => nurse.nurseId === nurseIdToRestore);

				if (nursesActual && typeof nurseIndex === "number" && nurseIndex !== -1) {
					const nurse = nursesActual[nurseIndex];
					if (nurse) {
						nurse.isPending = isHospital || !!isDraftMode;
						nurse.isPendingToDelete = false;
					}
				}
			} else {
				// Similar pattern for regular nurses array
				const nurses = tempData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].nurses;
				const nurseIndex = nurses.findIndex((nurse) => nurse.nurseId === nurseIdToRestore);

				if (nurseIndex !== -1) {
					const nurse = nurses[nurseIndex];
					nurse.isPending = isHospital || !!isDraftMode;
					nurse.isPendingToDelete = false;
					// if restored from predicted and actual exists then restore from actual also
					if (!restoredFromActual) {
						const nursesActual = tempData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo]?.nursesActual;
						if (nursesActual && nursesActual.length > 0) {
							const nurseActualIndex = nursesActual?.findIndex((nurse) => nurse.nurseId === nurseIdToRestore);
							if (typeof nurseActualIndex === "number" && nurseActualIndex !== -1) {
								const nurseActual = nursesActual[nurseActualIndex];
								if (nurseActual) {
									nurseActual.isPending = !!isDraftMode;
									nurseActual.isPendingToDelete = false;
								}
							}
						}
					}
				}
			}
			setHospitalData(tempData);
		}
	};

	const getWeekLoadColor = (weekId: number) => {
		return getColorForProperty("weekLoad", weekId);
	};
	const getWeekSeasonColor = (weekId: number) => {
		return getColorForProperty("weekSeason", weekId);
	};
	const getWeekForceColor = (weekId: number) => {
		return getColorForProperty("workForce", weekId);
	};
	const getWeekScoreColor = (weekId: number) => {
		return getColorForProperty("weekScore", weekId);
	};
	const getColorForProperty = (property: string, weekId: number) => {
		const data = hospitalData?.weeks[weekId];
		if (data && data[property as keyof WeekData] === "High") return "#FFA96A";
		if (data && data[property as keyof WeekData] === "Low") return "#5EB8F9";
		return NEUTRAL[500];
	};

	const getCensusColor = (shiftNo: number, dayNo: number) => {
		const data = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo];
		if (data && data.censusDifficulty.toLowerCase() === "high") return "#FFA96A";
		if (data && data.censusDifficulty.toLowerCase() === "low") return "#5EB8F9";
		return NEUTRAL[500];
	};

	const getScoreColor = (shiftNo: number, dayNo: number) => {
		const data = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo];
		if (data && data.staffDifficulty.toLowerCase() === "high") return "#FFA96A";
		if (data && data.staffDifficulty.toLowerCase() === "low") return "#5EB8F9";
		return NEUTRAL[500];
	};

	const getColorByDifficultyName = (difficulty: string | undefined) => {
		const difficultyLower = difficulty?.toLowerCase();
		if (difficultyLower === "high") return isHospitalView ? HOSPITAL_DEMO.NODE_HIGH : "#FFA96A";
		if (difficultyLower === "low") return isHospitalView ? HOSPITAL_DEMO.NODE_LOW : "#5EB8F9";
		return isHospitalView ? HOSPITAL_DEMO.NODE_MEDIUM : NEUTRAL[500];
	};

	const getNurseNameFromId = useCallback(
		(id: number) => {
			// Map lookup, not a roster scan: callers run this inside sort comparators and per-row filters.
			const nurse: Nurse | undefined = nursesMap.get(id);
			return nurse ? formatPersonFullName(nurse, shouldReverseNameFormat) : "";
		},
		[nursesMap, shouldReverseNameFormat],
	);

	const handleSuggestion = (suggestion: Suggestion, weekIndex: number, shiftNo: number, dayNo: number, shiftTimes?: ShiftTimes) => {
		if (suggestion.action === SuggestionAction.Swap) {
			onEnterSwap(shiftNo, dayNo, suggestion.otherShiftNo as number, suggestion.otherDayNo as number, suggestion.nurseId, suggestion.otherNurseId as number);
		} else if (suggestion.action === SuggestionAction.Remove) {
			removeNurseFromSpecificDate(weekIndex, dayNo, shiftNo, suggestion.nurseId);
		} else if (suggestion.action === SuggestionAction.Add) {
			const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(
				hospitalData,
				weekIndex,
				dayNo,
				shiftNo,
				Number(suggestion.nurseId),
				undefined,
				false,
				suggestion.requestId,
				shiftTimes,
			);

			if (nurseToAdd) {
				addNurseToShift(nurseToAdd, weekIndex, dayNo, shiftNo, false);
			}
		} else if (suggestion.action === SuggestionAction.Replace) {
			removeNurseFromSpecificDate(weekIndex, dayNo, shiftNo, suggestion.nurseId);
			const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(
				hospitalData,
				weekIndex,
				dayNo,
				shiftNo,
				Number(suggestion.otherNurseId),
				undefined,
				false,
				suggestion.requestId,
				shiftTimes,
			);

			if (nurseToAdd) {
				addNurseToShift(nurseToAdd, weekIndex, dayNo, shiftNo, false);
			}
		} else if (suggestion.action === SuggestionAction.Move) {
			moveNurseToSpecificDate(weekIndex, shiftNo, dayNo, suggestion.otherShiftNo as number, suggestion.otherDayNo as number, suggestion.nurseId);
		}
	};

	const parsNurseConflictError = useCallback(
		(parsedDetail: any | undefined): string => {
			if (!parsedDetail) {
				return t("error.adding.shifts");
			}

			const newNurseId = parsedDetail.nurse_id;
			const newNurseName = getNurseNameFromId(Number(newNurseId));

			if (parsedDetail.recurring_shift_id) {
				return t("error.recurring.shift.overlap", {
					nurseName: newNurseName,
					day: parsedDetail.start_date.toFormat("cccc"),
					date: parsedDetail.start_date.toFormat("hh a MMM d, yyyy"),
				});
			}
			return t("error.nurse.two.shifts", {
				nurseName: newNurseName,
				day: parsedDetail.start_date.toFormat("cccc"),
				date: parsedDetail.start_date.toFormat("hh a MMM d, yyyy"),
			});
		},
		[t, getNurseNameFromId],
	);

	const showErrorSnackbar = useCallback(
		(errorMessage: string, trackErrorVals: Record<string, any>, error?: any, autoHideDuration = 6_000, title: string = "") => {
			try {
				if (error) {
					console.warn(error);
					const errorDetail = error?.response?.data?.detail;
					let parsedDetail = typeof errorDetail === "string" ? JSON.parse(errorDetail) : errorDetail;
					parsedDetail = convertIsoDates(parsedDetail);
					switch (parsedDetail?.error_type) {
						case ConflictErrors.ConflictShift: {
							errorMessage = parsNurseConflictError(parsedDetail.message);
							autoHideDuration = 10_000;
							break;
						}
						case NurseActionsErrorTypes.DuplicateReplaceRequest:
						case NurseActionsErrorTypes.IntegrityError:
						case NurseActionsErrorTypes.UIVisibleError:
						case NurseActionsErrorTypes.MissingNurseEmailPhone: {
							const [key, ...params] = parsedDetail.message.split("|");
							if (params.length > 0) {
								const status = params[0];
								errorMessage = t(key, {
									status: t(`status.${status.toLowerCase()}`),
								});
							} else {
								errorMessage = t(parsedDetail.message);
							}
							autoHideDuration = 10_000;
							break;
						}
						case NurseActionsErrorTypes.RequestNotActive: {
							errorMessage = t("error.request.not.active");
						}
					}
				}
			} catch {}

			trackErrorEvent(title || "snackbarError", error, {
				...trackErrorVals,
				errorMessage: errorMessage,
			});
			showSnackbar(`${title ? `${title}:` : ""}${errorMessage}`, "error", autoHideDuration);
		},
		[showSnackbar, t, parsNurseConflictError],
	);

	const updateNurseNotes = (id: string, weekIndex: number, shiftIndex: number, dayIndex: number, newNotes: string, actual: boolean) => {
		setHospitalData((prevData) => {
			const newData = { ...prevData };
			const targetNurseArray = actual
				? newData.weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex].nursesActual
				: newData.weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex].nurses;
			const nurse = targetNurseArray?.find((n) => n.id === id);
			if (nurse) {
				nurse.notes = newNotes;
			}
			return newData;
		});
	};

	const updateShiftBonus = (
		id: string,
		weekIndex: number,
		shiftIndex: number,
		dayIndex: number,
		newBonus: number,
		actual?: boolean,
		isDraftMode = false,
	): void => {
		setHospitalData((prevData) => {
			const newData = { ...prevData };
			const targetNurseArray = actual
				? newData.weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex].nursesActual
				: newData.weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex].nurses;
			const nurse = targetNurseArray?.find((n) => n.id === id);
			if (nurse) {
				nurse.bonus = newBonus;
				if (isDraftMode) {
					nurse.isPendingToUpdate = true;
				}
			}
			return newData;
		});
	};

	const updateNurseLocationAndTimes = useCallback(
		({
			nurseInShiftId,
			weekIndex,
			shiftIndex,
			dayIndex,
			newStartTime,
			newEndTime,
			actual,
			newLocation,
			newJobId,
			newJobName,
			isDraftMode,
		}: {
			nurseInShiftId: string;
			weekIndex: number;
			shiftIndex: number;
			dayIndex: number;
			newStartTime?: DateTime;
			newEndTime?: DateTime;
			actual: boolean;
			newLocation?: string;
			newJobId?: string;
			newJobName?: string;
			isDraftMode?: boolean;
		}) => {
			setHospitalData((prevData) => {
				const newData = { ...prevData };
				const targetWeekArray = newData.weeks[weekIndex].weeklyShifts;
				// Locate the nurse by ID — caller's (shiftIndex, dayIndex) can point at the new
				// slot rather than where the nurse currently lives, which makes the find miss.
				let nurse: NurseInShift | undefined;
				let targetNurseArray: NurseInShift[] | undefined;
				for (const ws of targetWeekArray) {
					for (const day of ws.days) {
						const arr = actual ? day.nursesActual : day.nurses;
						const found = arr?.find((n) => n.id === nurseInShiftId);
						if (found) {
							nurse = found;
							targetNurseArray = arr;
							break;
						}
					}
					if (nurse) break;
				}
				const newShiftIndex =
					newStartTime && newEndTime
						? getShiftIndexByShiftTimes({
								startTime: newStartTime,
								endTime: newEndTime,
							})
						: shiftIndex;
				const targetNurseArrayNewShiftIndex = actual
					? targetWeekArray[newShiftIndex]?.days[dayIndex].nursesActual
					: targetWeekArray[newShiftIndex]?.days[dayIndex].nurses;
				if (nurse) {
					if (newStartTime && newEndTime) {
						nurse.startTime = newStartTime;
						nurse.endTime = newEndTime;
					}
					if (newLocation) {
						nurse.location = newLocation;
					}
					if (newJobId !== undefined) {
						nurse.jobId = newJobId;
						nurse.jobName = newJobName;
					}
					nurse.isPendingToUpdate = isDraftMode;
					nurse.shiftType = chosenDepartment.shift_types[newShiftIndex];
					if (targetNurseArray && targetNurseArrayNewShiftIndex && targetNurseArray !== targetNurseArrayNewShiftIndex) {
						targetNurseArray.splice(targetNurseArray.indexOf(nurse), 1);
						targetNurseArrayNewShiftIndex.push(nurse);
					}
				}
				return newData;
			});
		},
		[chosenDepartment.shift_types],
	);

	const updateNurseTimes = (
		nurseInShiftId: string,
		weekIndex: number,
		shiftIndex: number,
		dayIndex: number,
		newStartTime: DateTime,
		newEndTime: DateTime,
		actual: boolean,
		isDraftMode = false,
	) => {
		updateNurseLocationAndTimes({
			nurseInShiftId,
			weekIndex,
			shiftIndex,
			dayIndex,
			newStartTime,
			newEndTime,
			actual,
			isDraftMode,
		});
	};

	const updateNurseLocation = (id: string, weekIndex: number, shiftIndex: number, dayIndex: number, newLocation: string, actual: boolean) => {
		updateNurseLocationAndTimes({
			nurseInShiftId: id,
			weekIndex,
			shiftIndex,
			dayIndex,
			newLocation,
			actual,
		});
	};

	const updateNurseTrainingStatus = useCallback(
		(nurseInShiftId: string, weekIndex: number, shiftIndex: number, dayIndex: number, status: NurseTrainingStatus) => {
			setHospitalData((prevData) => {
				const { day, newData } = copyDayData(prevData, weekIndex, shiftIndex, dayIndex);
				day.nurses = day.nurses.map((nurseInShift) =>
					nurseInShift.id === nurseInShiftId
						? {
								...nurseInShift,
								trainingStatus: status,
								isPublished: false,
							}
						: nurseInShift,
				);
				day.nursesActual = day.nursesActual?.map((actualNurseInShift) =>
					actualNurseInShift.id === nurseInShiftId
						? {
								...actualNurseInShift,
								trainingStatus: status,
								isPublished: false,
							}
						: actualNurseInShift,
				);

				return newData;
			});
		},
		[],
	);

	const updateNurseChargeStatus = useCallback(
		(nurseInShiftId: string, weekIndex: number, shiftIndex: number, dayIndex: number, isCharge: boolean, isDraftMode = false) => {
			setHospitalData((prevData) => {
				const { day, newData } = copyDayData(prevData, weekIndex, shiftIndex, dayIndex);
				day.nurses = day.nurses.map((nurseInShift) =>
					nurseInShift.id === nurseInShiftId
						? {
								...nurseInShift,
								charge: isCharge,
								isPublished: false,
								...(isDraftMode ? { isPendingToUpdate: true } : {}),
							}
						: nurseInShift,
				);

				return newData;
			});
		},
		[],
	);

	const getNurseTransformedShifts = (nurseId: number): TransformedShift[] => {
		if (!hospitalData?.weeks?.length || !nurseId) return [];

		const nurse = hospitalData?.nurses.find((n: Nurse) => n.id === nurseId);
		if (!nurse) return [];

		// Create a map to store shifts by date to prevent duplicates
		const shiftsMap = new Map<string, TransformedShift[]>();

		// First, add all days off to the map
		if (nurse.employeeSpecificPreferences) {
			nurse.employeeSpecificPreferences
				.filter((pref: SpecificPreference) => pref.type === Preferences.Avoid)
				.forEach((pref: SpecificPreference) => {
					// The key is the staffing day at midnight, matching how shifts are keyed below — it used to
					// be the PTO's raw instant, so the "don't show time off where there is a shift" guard
					// beneath could never match and never fired.
					for (const day of getPtoStaffingDays(pref.start_date, pref.end_date, chosenDepartment.dayStart)) {
						const dateKey = day.toISO() as string;
						// Only add time off if there isn't already a shift on this day
						if (!shiftsMap.has(dateKey)) {
							shiftsMap.set(dateKey, [
								{
									date: day,
									hasDayRequest: false,
									hasNightRequest: false,
									isTimeOff: true,
									timeOffType: "Day Off",
									timeOffReason: pref.reason || "",
									isPreferredNot: false,
									isPreferred: false,
									requests: 0,
									departmentId: "",
								},
							]);
						}
					}
				});
		}

		// Then add all shifts from the schedule - these will override any time off
		hospitalData.weeks.forEach((week: WeekData) => {
			week.weeklyShifts.forEach((weeklyShift: Shift) => {
				weeklyShift.days.forEach((day: Day) => {
					const dateKey = day.date.toISO() as string;
					const nurseShifts: NurseInShift[] = day.nurses.filter((n: NurseInShift) => n.nurseId === nurse.id && !n.isPendingToDelete);

					// Count requests for this shift
					let requestCount = 0;

					day.nurses.forEach((n: NurseInShift) => {
						if (n.isPending && !n.isPendingToDelete && n.swapWith === nurse.id) {
							requestCount++;
						}
					});

					if (!nurseShifts.length && !shiftsMap.get(dateKey)) {
						shiftsMap.set(dateKey, [
							{
								date: day.date,
								hasDayRequest: false,
								hasNightRequest: false,
								isTimeOff: false,
								timeOffType: undefined,
								isPreferredNot: false,
								isPreferred: false,
								requests: requestCount,
								departmentId: "",
							},
						]);
					} else {
						nurseShifts.forEach((nurseShift: NurseInShift) => {
							// If there's a shift, it overrides any time off
							// Determine shift type based on time and type
							let shiftType = ShiftType.Day;
							if (
								weeklyShift.type === ShiftType.Night ||
								weeklyShift.type === ShiftType.Evening ||
								nurseShift.startTime.hour >= 18 ||
								nurseShift.startTime.hour < 6
							) {
								shiftType = ShiftType.Night;
							} else if (weeklyShift.type === ShiftType.FullDay) {
								shiftType = ShiftType.All;
							}

							// Before dayStart goes to the previous day, unless it is inside the scheduled grace.
							const adjustedDate = getShiftDisplayDate(nurseShift.startTime, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes);
							const adjustedDateKey = adjustedDate.toISO() as string;

							/**
							 * We should filter out any placeholder shifts (id == null) if there's a real shift on the same day
							 * this may happen if a nurse doesnt have day-time shifts but has night shifts, meaning that we will first create a placeholder and then we will have to discard it!
							 */
							const nurseTransformedShifts: TransformedShift[] =
								shiftsMap.get(adjustedDateKey)?.filter((transformedShift) => transformedShift.id != null) ?? [];

							shiftsMap.set(adjustedDateKey, [
								...nurseTransformedShifts,
								{
									id: nurseShift.id,
									date: adjustedDate,
									startTime: nurseShift.startTime,
									endTime: nurseShift.endTime,
									location: nurseShift.location || "",
									shiftType,
									isCharge: nurseShift.charge,
									hasDayRequest: false,
									hasNightRequest: false,
									isTimeOff: false, // Never show time off if there's a shift
									timeOffType: undefined,
									timeOffReason: undefined,
									isPreferredNot: false,
									isPreferred: false,
									requests: requestCount,
									bonus: nurseShift.bonus,
									departmentId: nurseShift.departmentId || "",
									notes: nurseShift.notes,
								},
							]);
						});
					}
				});
			});
		});

		// Convert map to array and sort by date
		return Array.from(shiftsMap.values())
			.flat()
			.sort((a: TransformedShift, b: TransformedShift) => a.date.diff(b.date).toMillis());
	};

	const nurseId = useMemo(() => {
		if (!userInfo.nurses_info) return undefined;
		for (const nurseInfo of userInfo.nurses_info) {
			if (nurseInfo.departments.find((dept) => dept === chosenDepartment.department_id)) {
				return nurseInfo.nurse.id;
			}
		}
	}, [chosenDepartment, userInfo])!;

	const transformedShifts = useMemo(
		() => getNurseTransformedShifts(nurseId),
		[hospitalData, nurseId !== undefined, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes],
	);

	const userIdNurseMap = useMemo<{ [userId: string]: Nurse }>(() => {
		return hospitalData.nurses.reduce<{ [userId: string]: Nurse }>((accu, current) => {
			if (current.userId) {
				accu[current.userId] = current;
			}
			return accu;
		}, {});
	}, [hospitalData.nurses]);

	const latestDateInWeeksData = useMemo(() => {
		return hospitalData.weeks
			.at(-1)
			?.weeklyShifts.map((value) => value.days.at(-1)?.date)
			.filter((date) => date !== undefined)
			.sort((a, b) => {
				if (a && b) {
					return a < b ? -1 : 1;
				}
				return 0;
			})
			.at(-1);
	}, [hospitalData.weeks]);

	const departmentEmrType = useMemo<EmrType | undefined>(() => (chosenDepartment ? resolveActiveEmrType(chosenDepartment) : undefined), [chosenDepartment]);

	const roleAverageWage = useMemo<Partial<Record<NurseFullRole, string>>>(() => {
		const averageWage: Partial<Record<NurseFullRole, string>> = {};

		if (!hospitalData.nurses || hospitalData.nurses.length === 0) return averageWage;

		const roleGroups: Partial<Record<NurseFullRole, number[]>> = {};

		hospitalData.nurses.forEach((nurse) => {
			const currentWage = nurse.baseWages?.find((bw) => bw.wage > 0 && bw.wageType !== WageType.Annually && !bw.endTime);
			if (currentWage && nurse.role) {
				if (!roleGroups[nurse.role]) {
					roleGroups[nurse.role] = [];
				}
				roleGroups[nurse.role]!.push(currentWage.wage);
			}
		});

		Object.keys(roleGroups).forEach((role) => {
			const wages = roleGroups[role as NurseFullRole];

			if (wages && wages.length > 0) {
				const sum = wages.reduce((acc, wage) => acc + wage, 0);
				averageWage[role as NurseFullRole] = Number(sum / wages.length).toFixed(2);
			}
		});

		return averageWage;
	}, [hospitalData.nurses]);

	const getDepartmentDisplayName = (departmentId: string): string =>
		userInfo.departments.find((dept) => dept.department_id === departmentId)?.display_name || "";

	const getEarliestShiftDate = useCallback((): DateTime | null => {
		return hospitalData.weeks.at(0)?.weeklyShifts.at(0)?.days.at(0)?.date || null;
	}, [hospitalData.weeks]);

	const isNurseWorking = useCallback(
		(nurseId: number, date: DateTime): boolean => {
			if (!hospitalData?.weeks?.length) return false;
			return hospitalData.weeks.some((weeklyShifts) => {
				if (!weeklyShifts?.weeklyShifts) return false;
				return weeklyShifts.weeklyShifts.some((shift) =>
					shift.days.some((day) => day.date.hasSame(date, "day") && day.nurses?.some((n) => n.nurseId === nurseId)),
				);
			});
		},
		[hospitalData.weeks],
	);

	const findEligibleNurse = useCallback(
		(originalNurse: Nurse, hospitalDataArg: HospitalData, removeDate: DateTime): Nurse | undefined => {
			if (!hospitalDataArg?.nurses || !originalNurse) return undefined;
			// Active in the chosen department (per-department source of truth), not the legacy global
			// nurse.active — a float-pool nurse inactive at a sibling facility is still eligible here.
			const nursesWithSameRole = hospitalDataArg.nurses.filter(
				(n) => n.role === originalNurse.role && n.id !== originalNurse.id && isNurseActiveInDepartment(n, chosenDepartment.department_id),
			);
			const nursesNotWorkingOnDate = nursesWithSameRole.filter((n) => !isNurseWorking(n.id, removeDate));
			return nursesNotWorkingOnDate.length > 0 ? nursesNotWorkingOnDate[0] : undefined;
		},
		[isNurseWorking, chosenDepartment.department_id],
	);

	const findNurseShiftDetails = useCallback(
		(
			originalNurse: Nurse,
			hospitalDataArg: HospitalData,
			removeDate: DateTime,
		): { weekIndex: number; shiftNo: number; dayNo: number; nurseInShift: NurseInShift } | undefined => {
			if (!hospitalDataArg?.weeks || hospitalDataArg.weeks.length === 0) return undefined;
			const removeDateString = removeDate.toFormat("yyyy-MM-dd");
			for (let weekIndex = 0; weekIndex < hospitalDataArg.weeks.length; weekIndex++) {
				const week = hospitalDataArg.weeks[weekIndex];
				if (!week?.weeklyShifts) continue;
				for (let shiftNo = 0; shiftNo < week.weeklyShifts.length; shiftNo++) {
					const shift = week.weeklyShifts[shiftNo];
					if (!shift?.days) continue;
					for (let dayNo = 0; dayNo < shift.days.length; dayNo++) {
						const day = shift.days[dayNo];
						const dayString = day.date?.toFormat("yyyy-MM-dd");
						if (dayString === removeDateString) {
							const nurseInShift = day.nurses?.find((n) => n.nurseId === originalNurse.id && !n.isPendingToDelete);
							if (nurseInShift) {
								return { weekIndex, shiftNo, dayNo, nurseInShift };
							}
						}
					}
				}
			}
			return undefined;
		},
		[],
	);

	/**
	 * Finds the most specific matching DepartmentLocation for a nurse
	 * Matches based on: location name, roles, and shift times
	 * @param nurse - The nurse to find a location for
	 * @param locations - All department locations
	 * @returns The matching DepartmentLocation or undefined
	 */
	const findDepartmentLocationForNurse = useCallback(
		(nurse: NurseInShift, locations: DepartmentLocation[]): DepartmentLocation | undefined => {
			if (!nurse.location) return undefined;

			// Filter locations by location name first
			const locationsWithSameName = locations.filter((loc) => loc.location === nurse.location);

			if (locationsWithSameName.length === 0) return undefined;
			if (locationsWithSameName.length === 1) return locationsWithSameName[0];

			// Multiple locations with same name - need to match by role/job and shift times
			// Priority: 1. Match role/job AND shift times, 2. Match role/job only, 3. Match shift times only, 4. First match
			const nurseRole = nurse.role;
			const nurseJobId = nurse.jobId;
			const nurseStartTime = nurse.startTime?.toFormat("HH:mm");
			const nurseEndTime = nurse.endTime?.toFormat("HH:mm");

			const matchesRoleOrJob = (loc: DepartmentLocation): boolean => {
				if (jobManagementEnabled && loc.jobId) {
					return !!nurseJobId && loc.jobId === nurseJobId;
				}
				return loc.roles.includes("all") || (!!nurseRole && loc.roles.includes(nurseRole));
			};

			// Try to find exact match (role/job + shift times)
			const exactMatch = locationsWithSameName.find((loc) => {
				return matchesRoleOrJob(loc) && nurseStartTime === loc.shift_start && nurseEndTime === loc.shift_end;
			});

			if (exactMatch) return exactMatch;

			// Try to find role/job match only
			const roleMatch = locationsWithSameName.find(matchesRoleOrJob);

			if (roleMatch) return roleMatch;

			// Try to find shift times match only
			const shiftTimesMatch = locationsWithSameName.find((loc) => nurseStartTime === loc.shift_start && nurseEndTime === loc.shift_end);

			if (shiftTimesMatch) return shiftTimesMatch;

			// Return first location as fallback
			return locationsWithSameName[0];
		},
		[jobManagementEnabled],
	);

	useEffect(() => {
		/*Handle setting default zone for Luxon*/
		Settings.defaultZone = timezone;
	}, [timezone]);

	const getNurseToWeeklyShifts = useCallback(
		(nurseId: Nurse["id"]): Shift[] => {
			const nurse = hospitalData.nurses.find((n) => n.id === nurseId);
			if (!nurse) {
				return [];
			}

			const { weeklyShifts = [] } = hospitalData.weeks.at(weekIndex) || {};
			return weeklyShifts.map<Shift>((shift) => ({
				...shift,
				days: shift.days.map<Day>((day) => ({
					...day,
					nurses: day.nurses.filter((n) => n.nurseId === nurse.id),
					nursesActual: day.nursesActual?.filter((n) => n.nurseId === nurse.id),
				})),
			}));
		},
		[hospitalData, weekIndex],
	);

	if (isInitialLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<LoadingFallback />
			</Box>
		);
	}

	const contextValue = {
		hospitalData,
		setHospitalData,
		swapNurseId,
		swapNurseWithId,
		swapDayNo,
		swapDayNoWith,
		swapShiftNo,
		chosenDepartment,
		locationsMap,
		timezone,
		isSnf,
		isAlf,
		isHospital,
		isHospitalDemo,
		isHospitalView,
		isIcuTeleDemo,
		isSnfDemo,
		isAlfDemo,
		isAutoScheduler,
		isInitialLoading,
		isLowry,
		isColonial,
		setChosenDepartment,
		setSwapNurseId,
		setSwapNurseWithId,
		setSwapDayNo,
		setSwapDayNoWith,
		setSwapShiftNo,
		setSwapShiftNoWith,
		getWeekLoadColor,
		getWeekSeasonColor,
		getWeekForceColor,
		getWeekScoreColor,
		getCensusColor,
		getScoreColor,
		addNurseToShift,
		prepareDataForAddNurse,
		acceptNurseChanges,
		addMessageToNurse,
		declineNurseChanges,
		removeNurseFromSpecificDate,
		updateNurse,
		moveNurseToSpecificDate,
		swapNurseFromSpecificDate,
		getColorByDifficultyName,
		getWeekIndexByDateTime,
		getDayIndexByDateTime,
		getWeekIndexByDate,
		getDayIndexByDate,
		restoreNurseToSpecificDate,
		deepCloneObject,
		removeNurseCompletelyFromShift,
		removeNursesShiftsCompletely,
		addNewDBNurseShifts,
		onEnterSwap,
		reorderNurses,
		getNurseNameFromId,
		implementedChanges,
		setImplementedChanges,
		handleSuggestion,
		weekI,
		setWeekI,
		getNurseShifts,
		refreshDepartmentData,
		refreshDepartmentWageData,
		refreshNursesData,
		isNurseInFutureShifts,
		isNurseInFutureRecurringShifts,
		getNurseWeekendShifts,
		getNurseInShiftDetailsById,
		getNurseRDOs,
		getNurseRole,
		updateNurseLocation,
		updateNurseNotes,
		updateNurseTimes,
		updateShiftBonus,
		showErrorSnackbar,
		getShiftTimes,
		dateIndexMap,
		calculateShiftTimes: calculateShiftTimesWithTZ,
		updateNurseChargeStatus,
		updateNurseTrainingStatus,
		transformedShifts,
		getNurseTransformedShifts,
		getNurseFormattedName,
		getNurseById,
		scheduledShiftsById,
		activeBedsideNurse,
		getShiftDate,
		departmentNurseTypes,
		userIdNurseMap,
		holidayOptions,
		getRoleGroupCounts,
		getJobGroupCounts,
		federalHolidaysUSASet,
		latestDateInWeeksData,
		roleAverageWage,
		departmentEmrType,
		getDepartmentDisplayName,
		getNurseShiftsInTimeFrame,
		getEarliestShiftDate,
		isNurseWorking,
		findEligibleNurse,
		findNurseShiftDetails,
		findDepartmentLocationForNurse,
		calculateDefaultTimesAndLocationForStaff,
		isAnyDemo: isHospitalDemo || isHospitalView || isSnfDemo || isAlfDemo,
		isRealDemo,
		updateNurseLocationAndTimes,
		getNurseToWeeklyShifts,
		isMedicalDepartment: chosenDepartment.isSynergyMode ?? false,
		getNurseRoleEnum,
		wageData,
		getWeekRange,
	};
	return <HospitalDataContext.Provider value={contextValue}>{children}</HospitalDataContext.Provider>;
};

export const useHospitalData = () => {
	return useContext(HospitalDataContext);
};

export function copyDayData(prevData: HospitalData, weekIndex: number, shiftNo: number, dayNo: number) {
	const newData = { ...prevData, weeks: [...prevData.weeks] };

	newData.weeks[weekIndex] = {
		...prevData.weeks[weekIndex],
		weeklyShifts: [...prevData.weeks[weekIndex].weeklyShifts],
	};

	newData.weeks[weekIndex].weeklyShifts[shiftNo] = {
		...prevData.weeks[weekIndex].weeklyShifts[shiftNo],
		days: [...prevData.weeks[weekIndex].weeklyShifts[shiftNo].days],
	};

	const days = newData.weeks[weekIndex].weeklyShifts[shiftNo].days;
	const day = { ...days[dayNo] };
	days[dayNo] = day;
	return { day, newData };
}

/** A copy of `weeks` whose shift nurses (scheduled and actual) carry their names in the given order. */
function withShiftNamesInOrder(nurses: Nurse[], weeks: WeekData[], lastNameFirst: boolean): WeekData[] {
	const nurseMap = new Map(nurses.map((nurse) => [nurse.id, nurse]));
	const rename = <T extends { nurseId: number; name: string }>(shiftNurse: T): T => {
		const fullNurse = nurseMap.get(shiftNurse.nurseId);
		return fullNurse ? { ...shiftNurse, name: formatPersonFullName(fullNurse, lastNameFirst) } : shiftNurse;
	};
	return weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((shift) => ({
			...shift,
			days: shift.days.map((day) => ({
				...day,
				nurses: day.nurses.map(rename),
				...(day.nursesActual && { nursesActual: day.nursesActual.map(rename) }),
			})),
		})),
	}));
}

function updateNurses(nurses: Nurse[], weeks: WeekData[], lastNameFirst: boolean) {
	if (!nurses.length) return;

	const nurseMap = new Map(nurses.map((nurse) => [nurse.id, nurse]));

	weeks.forEach((week) => {
		week.weeklyShifts.forEach((shift) => {
			shift.days.forEach((day) => {
				day.nurses = day.nurses.filter((nurse) => {
					const fullNurse = nurseMap.get(nurse.nurseId);
					if (fullNurse === undefined) return false;
					nurse.role = fullNurse.role;
					nurse.name = formatPersonFullName(fullNurse, lastNameFirst);
					nurse.type = fullNurse.employmentType as EmploymentType;
					if (nurse.jobId && fullNurse.jobs?.length) {
						const job = fullNurse.jobs.find((j) => j.id === nurse.jobId);
						if (job) nurse.jobName = job.displayName;
					}
					return true;
				});
				if (day.nursesActual) {
					day.nursesActual = day.nursesActual.filter((nurse) => {
						const fullNurse = nurseMap.get(nurse.nurseId);
						if (fullNurse === undefined) return false;
						nurse.role = fullNurse.role;
						nurse.name = formatPersonFullName(fullNurse, lastNameFirst);
						nurse.type = fullNurse.employmentType as EmploymentType;
						if (nurse.jobId && fullNurse.jobs?.length) {
							const job = fullNurse.jobs.find((j) => j.id === nurse.jobId);
							if (job) nurse.jobName = job.displayName;
						}
						return true;
					});
				}
			});
		});
	});
}

function acceptActualChange(prevData: HospitalData, currWeek: WeekData, targetShift: Day, nurseId: number, weekI: number, shiftNo: number, dayNo: number) {
	const nurse = targetShift.nursesActual?.find((n: ActualNurse) => n.nurseId === nurseId);
	if (!nurse) {
		console.log(`Error tried to acceptNurseChanges for week ${weekI} nurse ${nurseId}, shiftNo ${shiftNo}, dayNo ${dayNo}, is_actual ${true}`);
		return prevData;
	}
	if (nurse.isPendingToDelete) {
		targetShift.nursesActual = targetShift.nursesActual?.filter((n: ActualNurse) => n.nurseId !== nurseId);
	}
	// Handle regular addition or confirmation scenario
	else {
		nurse.isPending = false;
		nurse.isPublished = true;
	}

	const updatedWeeks = [...prevData.weeks];
	updatedWeeks[weekI] = currWeek;
	return { ...prevData, weeks: updatedWeeks };
}

export const getNonDefaultShiftTime = (nurseInShift: NurseInShift, day: Day, shiftNo: number, chosenDepartment: Department, timezone: string) => {
	if (!nurseInShift.startTime || !nurseInShift.endTime) {
		return null;
	}

	const defaultTimes = calculateShiftTimes({
		day,
		shiftNo,
		nurseRole: nurseInShift.role,
		chosenDepartment,
		location: nurseInShift.location,
	});
	if (nurseInShift.startTime.valueOf() !== defaultTimes.startTime.valueOf() || nurseInShift.endTime.valueOf() !== defaultTimes.endTime.valueOf()) {
		return {
			actualStart: nurseInShift.startTime,
			actualEnd: nurseInShift.endTime,
		};
	}
	return null;
};
